import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import axiosInstance from '../api';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Well-known port → service label ─────────────────────────────────────────
const WELL_KNOWN_PORTS = {
  21: 'FTP', 22: 'SSH', 23: 'Telnet', 25: 'SMTP', 53: 'DNS',
  80: 'HTTP', 110: 'POP3', 143: 'IMAP', 443: 'HTTPS', 465: 'SMTPS',
  587: 'Submission', 993: 'IMAPS', 995: 'POP3S', 1433: 'MSSQL',
  3306: 'MySQL', 3389: 'RDP', 5432: 'PostgreSQL', 5900: 'VNC',
  6379: 'Redis', 8080: 'HTTP-Alt', 8443: 'HTTPS-Alt',
  8888: 'HTTP-Alt', 9200: 'Elasticsearch', 27017: 'MongoDB',
};

// ─── Severity config ─────────────────────────────────────────────────────────
const SEV = {
  CRITICAL: { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', label: 'CRITICAL' },
  HIGH:     { color: '#ea580c', bg: '#fff7ed', border: '#fdba74', label: 'HIGH' },
  MEDIUM:   { color: '#d97706', bg: '#fffbeb', border: '#fcd34d', label: 'MEDIUM' },
  LOW:      { color: '#16a34a', bg: '#f0fdf4', border: '#86efac', label: 'LOW' },
  UNKNOWN:  { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', label: 'N/A' },
};
const getSev = (s) => SEV[(s || '').toUpperCase()] || SEV.UNKNOWN;

function countCves(report) {
  const counts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
  for (const sub of (report?.subdomains || [])) {
    for (const ip of (sub.ipAddresses || [])) {
      for (const port of (ip.ports || [])) {
        for (const app of (port.applications || [])) {
          for (const cve of (app.cveMatches || [])) {
            const k = (cve.severity || '').toUpperCase();
            if (k in counts) counts[k]++;
          }
        }
      }
    }
  }
  return counts;
}

function scanDuration(scan) {
  if (!scan?.startTime || !scan?.endTime) return '—';
  const ms = new Date(scan.endTime) - new Date(scan.startTime);
  const min = Math.floor(ms / 60000);
  const sec = Math.floor((ms % 60000) / 1000);
  return min > 0 ? `${min}m ${sec}s` : `${sec}s`;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// ─── Module / phase helpers ───────────────────────────────────────────────────
// phase: LIGHT = passive recon (light scan), FULL = active scan, DEEP = M1-M7 deep probe
const MODULE_CATEGORIES = [
  { id: 'ALL',         label: 'All',             color: '#4c75af', phase: null    },
  // Light / passive recon
  { id: 'RECON',       label: 'Passive Recon',   color: '#0891b2', phase: 'LIGHT' },
  // Full scan
  { id: 'HEADERS',     label: 'HTTP Headers',    color: '#64748b', phase: 'FULL'  },
  { id: 'PATHS',       label: 'Exposed Paths',   color: '#7c3aed', phase: 'FULL'  },
  { id: 'NETWORK',     label: 'Network / TLS',   color: '#0369a1', phase: 'FULL'  },
  { id: 'WEBAPP',      label: 'Web App',         color: '#c026d3', phase: 'FULL'  },
  // Deep scan (M1-M7)
  { id: 'GIT',         label: 'Git Exposure',    color: '#7c3aed', phase: 'DEEP'  },
  { id: 'ENV',         label: 'Env Secrets',     color: '#dc2626', phase: 'DEEP'  },
  { id: 'CREDENTIALS', label: 'Default Creds',   color: '#16a34a', phase: 'DEEP'  },
  { id: 'ACTUATOR',    label: 'Actuator',        color: '#ea580c', phase: 'DEEP'  },
  { id: 'CORS',        label: 'CORS',            color: '#0891b2', phase: 'DEEP'  },
  { id: 'GHOSTCAT',    label: 'Ghostcat / AJP',  color: '#b45309', phase: 'DEEP'  },
  { id: 'CLOUD',       label: 'Cloud / S3',      color: '#d97706', phase: 'DEEP'  },
];

const DEEP_MODULE_IDS = new Set(['GIT','ENV','CREDENTIALS','ACTUATOR','CORS','GHOSTCAT','CLOUD']);

function cveModuleCategory(cveId) {
  if (!cveId) return 'RECON';
  const id = cveId.toUpperCase();
  // Deep scan modules (M1-M7)
  if (id.startsWith('GIT-'))       return 'GIT';
  if (id.startsWith('ENV-'))       return 'ENV';
  if (id.startsWith('DEFCREDS-'))  return 'CREDENTIALS';
  if (id.startsWith('ACTUATOR-'))  return 'ACTUATOR';
  if (id.startsWith('CORS-'))      return 'CORS';
  if (id.startsWith('GHOSTCAT-'))  return 'GHOSTCAT';
  if (id.startsWith('S3WRITE-'))   return 'CLOUD';
  // Full scan – active web checks
  if (id.startsWith('HDR-'))                                              return 'HEADERS';
  if (id.startsWith('PATH-') || id.startsWith('TECH-'))                  return 'PATHS';
  if (id.startsWith('WAF-') || id.startsWith('FAVICON-') || id.startsWith('JSSECRET-')) return 'WEBAPP';
  if (id.startsWith('NSE-') || id.startsWith('JARM-') || id.startsWith('TLSSAN-'))      return 'NETWORK';
  // Light / passive recon
  if (id.startsWith('BUCKET-') || id.startsWith('CLOUD-'))               return 'CLOUD';
  if (id.startsWith('EMAIL-') || id.startsWith('CNAME-') || id.startsWith('EXPIRY-') ||
      id.startsWith('HISTDNS-') || id.startsWith('GITHUB-') || id.startsWith('ASN-'))   return 'RECON';
  return 'WEBAPP';
}

function getCvePhase(cveId) {
  const cat = cveModuleCategory(cveId);
  if (cat === 'RECON') return 'LIGHT';
  if (DEEP_MODULE_IDS.has(cat)) {
    // S3WRITE- is deep; BUCKET-/CLOUD- is light recon
    const id = (cveId || '').toUpperCase();
    if (cat === 'CLOUD' && !id.startsWith('S3WRITE-')) return 'LIGHT';
    return 'DEEP';
  }
  return 'FULL';
}

function moduleCategoryLabel(cveId) {
  const cat = cveModuleCategory(cveId);
  return MODULE_CATEGORIES.find(m => m.id === cat) || { id: 'WEBAPP', label: 'Web App', color: '#c026d3' };
}

// ─── PDF Export ───────────────────────────────────────────────────────────────
const SEV_ORDER = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, INFO: 4 };
const SEV_RGB = {
  CRITICAL: [220, 38,  38],
  HIGH:     [234, 88,  12],
  MEDIUM:   [217, 119,  6],
  LOW:      [ 22, 163, 74],
  INFO:     [107, 114, 128],
};

function exportPdf(reportData, domainName, scanDate) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const PW = 210, PH = 297, M = 15;
  const W = PW - M * 2;
  const now = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });

  // ── Flatten & sort all findings ──────────────────────────────────────────
  const findings = [];
  const falsePositives = [];
  for (const sub of reportData.subdomains || []) {
    for (const ip of sub.ipAddresses || []) {
      for (const port of ip.ports || []) {
        if (port.state !== 'open') continue;
        for (const app of port.applications || []) {
          for (const cve of app.cveMatches || []) {
            const penTest = cve.penTestResult?.result || null;
            // Domain-level findings (expiry/historical-DNS/ASN/bucket/GitHub) are stored under a
            // synthetic "0.0.0.0" IP + protocol-number port so they have somewhere to hang off of —
            // that's an internal-storage detail, not a real network location. Surface the actual
            // evidence locus the backend embedded in the description instead (CATAAM-08).
            const checkedMatch = /\[checked:\s*([^\]]+)\]\s*$/.exec(cve.description || '');
            const location = ip.address === '0.0.0.0'
              ? (checkedMatch ? checkedMatch[1] : sub.name)
              : `${sub.name}\n${ip.address}:${port.portNumber}`;
            const description = checkedMatch
              ? cve.description.slice(0, checkedMatch.index)
              : (cve.description || '');
            const entry = {
              severity:    (cve.severity || 'INFO').toUpperCase(),
              cveId:       cve.cveId || '—',
              appName:     app.name || '—',
              location,
              description: description.replace(/[ \t]+/g, ' ').trim(),
              penTest,
            };
            if (penTest === 'NOT_VULNERABLE') {
              falsePositives.push(entry);
            } else {
              findings.push(entry);
            }
          }
        }
      }
    }
  }
  // ── Consolidate repeated header/path findings ────────────────────────────
  // Security headers (HDR-) and exposed paths (PATH-) fire once per URL, so a domain with
  // 15 subdomains each missing HSTS produces 15 identical rows. Group them into one row
  // listing all affected subdomains — cuts report noise from 8 pages to ~3.
  const _groups = new Map();
  const _solo   = [];
  for (const f of findings) {
    if (f.cveId.startsWith('HDR-') || f.cveId.startsWith('PATH-')) {
      if (!_groups.has(f.cveId)) _groups.set(f.cveId, { ...f, _hosts: new Set() });
      const g = _groups.get(f.cveId);
      g._hosts.add(f.location.split('\n')[0]);
      if ((SEV_ORDER[f.severity] ?? 9) < (SEV_ORDER[g.severity] ?? 9)) g.severity = f.severity;
    } else {
      _solo.push(f);
    }
  }
  const consolidatedFindings = [..._solo];
  for (const [, g] of _groups) {
    const hosts = [...g._hosts];
    const n = hosts.length;
    consolidatedFindings.push({
      ...g,
      location: n === 1
        ? hosts[0]
        : `${i18n.t('AsmDashboard.pdfNSubdomains', { count: n })}\n${hosts.slice(0, 4).join('\n')}${n > 4 ? `\n${i18n.t('AsmDashboard.pdfPlusMore', { count: n - 4 })}` : ''}`,
    });
  }
  consolidatedFindings.sort((a, b) => (SEV_ORDER[a.severity] ?? 9) - (SEV_ORDER[b.severity] ?? 9));

  const counts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, INFO: 0 };
  consolidatedFindings.forEach(f => { if (f.severity in counts) counts[f.severity]++; });
  const totalFindings  = consolidatedFindings.length;
  const confirmedDeep  = consolidatedFindings.filter(f => f.penTest === 'EXPLOITED').length;
  const fpCount        = falsePositives.length;

  // ── Page 1: Cover ────────────────────────────────────────────────────────
  // Navy header band
  doc.setFillColor(30, 58, 95);
  doc.rect(0, 0, PW, 52, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text(i18n.t('AsmDashboard.pdfTitle'), M, 24);

  doc.setFontSize(13);
  doc.setFont('helvetica', 'normal');
  doc.text(domainName, M, 34);

  doc.setFontSize(9);
  doc.setTextColor(180, 200, 230);
  doc.text(i18n.t('AsmDashboard.pdfGenerated', { date: now }), M, 43);
  if (scanDate) doc.text(i18n.t('AsmDashboard.pdfLastScan', { date: scanDate }), M + 90, 43);

  // ── Executive summary box ────────────────────────────────────────────────
  let y = 64;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(30, 58, 95);
  doc.text(i18n.t('AsmDashboard.pdfExecutiveSummary'), M, y);
  y += 7;

  // Asset strip
  const assetMetrics = [
    { label: i18n.t('AsmDashboard.subdomains'), value: (reportData.subdomains || []).length },
    { label: i18n.t('AsmDashboard.ipAddresses'), value: (reportData.subdomains || []).reduce((n, s) => n + (s.ipAddresses || []).length, 0) },
    { label: i18n.t('AsmDashboard.openPorts'), value: (reportData.subdomains || []).reduce((n, s) => n + (s.ipAddresses || []).reduce((m, ip) => m + (ip.ports || []).filter(p => p.state === 'open').length, 0), 0) },
    { label: i18n.t('AsmDashboard.totalFindings'), value: totalFindings },
  ];
  const boxW = (W - 9) / 4;
  assetMetrics.forEach((m, i) => {
    const bx = M + i * (boxW + 3);
    doc.setFillColor(245, 247, 250);
    doc.roundedRect(bx, y, boxW, 22, 2, 2, 'F');
    doc.setTextColor(30, 58, 95);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(String(m.value), bx + boxW / 2, y + 11, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(107, 114, 128);
    doc.text(m.label, bx + boxW / 2, y + 18, { align: 'center' });
  });
  y += 30;

  // Severity breakdown
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(30, 58, 95);
  doc.text(i18n.t('AsmDashboard.pdfFindingsBySeverity'), M, y);
  y += 6;

  const sevKeys = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];
  const sevBoxW = (W - 12) / 5;
  sevKeys.forEach((sev, i) => {
    const bx = M + i * (sevBoxW + 3);
    const [r, g, b] = SEV_RGB[sev];
    doc.setFillColor(r, g, b);
    doc.roundedRect(bx, y, sevBoxW, 20, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text(String(counts[sev]), bx + sevBoxW / 2, y + 11, { align: 'center' });
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(sev, bx + sevBoxW / 2, y + 17, { align: 'center' });
  });
  y += 28;

  // Risk narrative
  const topCrits = consolidatedFindings.filter(f => f.severity === 'CRITICAL').length;
  const topHighs  = consolidatedFindings.filter(f => f.severity === 'HIGH').length;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(55, 65, 81);
  const fpNote = fpCount > 0
    ? ' ' + i18n.t('AsmDashboard.pdfNarrativeFpNote', { count: fpCount })
    : '';
  const deepNote = confirmedDeep > 0
    ? ' ' + i18n.t('AsmDashboard.pdfNarrativeDeepNote', { count: confirmedDeep })
    : '';
  const narrative = topCrits > 0
    ? i18n.t('AsmDashboard.pdfNarrativeCritical', { crit: topCrits, high: topHighs, count: topCrits + topHighs }) + deepNote + fpNote
    : topHighs > 0
    ? i18n.t('AsmDashboard.pdfNarrativeHigh', { count: topHighs }) + deepNote + fpNote
    : i18n.t('AsmDashboard.pdfNarrativeNone') + fpNote;
  const lines = doc.splitTextToSize(narrative, W);
  doc.text(lines, M, y);
  y += lines.length * 5 + 8;

  // ── Findings table ───────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(30, 58, 95);
  doc.text(i18n.t('AsmDashboard.pdfFindingsDetail'), M, y);
  y += 4;

  if (consolidatedFindings.length === 0) {
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128);
    doc.text(i18n.t('AsmDashboard.pdfNoFindings'), M, y + 8);
  } else {
    autoTable(doc, {
      startY: y,
      margin: { left: M, right: M },
      head: [[i18n.t('AsmDashboard.pdfColSeverity'), i18n.t('AsmDashboard.pdfColIdFinding'), i18n.t('AsmDashboard.pdfColLocation'), i18n.t('AsmDashboard.pdfColDescription')]],
      body: consolidatedFindings.map(f => [
        f.severity,
        `${f.cveId}\n${f.appName}`,
        f.location,
        f.description.length > 300 ? f.description.slice(0, 297) + '…' : f.description,
      ]),
      columnStyles: {
        0: { cellWidth: 20, fontStyle: 'bold', halign: 'center', fontSize: 7.5 },
        1: { cellWidth: 38, fontSize: 7.5 },
        2: { cellWidth: 36, fontSize: 7.5 },
        3: { cellWidth: W - 20 - 38 - 36, fontSize: 7.5 },
      },
      headStyles: {
        fillColor: [30, 58, 95],
        textColor: 255,
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center',
      },
      bodyStyles: { valign: 'top', lineColor: [229, 231, 235], lineWidth: 0.2 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      didParseCell(data) {
        if (data.section === 'body' && data.column.index === 0) {
          const sev = data.cell.raw;
          const [r, g, b] = SEV_RGB[sev] || [107, 114, 128];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fillColor = [r, g, b];
        }
        // Highlight exploitable pen-test rows
        if (data.section === 'body') {
          const f = consolidatedFindings[data.row.index];
          if (f?.penTest === 'EXPLOITED') {
            data.cell.styles.fillColor = [254, 242, 242];
          }
        }
      },
    });
  }

  // ── Appendix: False Positives (deep scan ruled out) ─────────────────────
  if (falsePositives.length > 0) {
    doc.addPage();
    let ay = M + 5;

    doc.setFillColor(245, 247, 250);
    doc.rect(0, 0, PW, 22, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(30, 58, 95);
    doc.text(i18n.t('AsmDashboard.pdfAppendixTitle'), M, 14);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(75, 85, 99);
    const fpIntro = i18n.t('AsmDashboard.pdfAppendixIntro', { count: falsePositives.length });
    const fpLines = doc.splitTextToSize(fpIntro, W);
    doc.text(fpLines, M, ay + 10);
    ay += fpLines.length * 5 + 20;

    // Group FPs by cveId for a concise table
    const fpGrouped = new Map();
    for (const f of falsePositives) {
      if (!fpGrouped.has(f.cveId)) fpGrouped.set(f.cveId, { ...f, _count: 0, _hosts: [] });
      const g = fpGrouped.get(f.cveId);
      g._count++;
      g._hosts.push(f.location.split('\n')[0]);
    }
    const fpRows = [...fpGrouped.values()].map(g => [
      g.severity,
      g.cveId,
      g._count > 1 ? i18n.t('AsmDashboard.pdfNHosts', { count: g._count }) : g._hosts[0],
      i18n.t('AsmDashboard.pdfRuledOut', { description: g.description.length > 200 ? g.description.slice(0, 197) + '…' : g.description }),
    ]);

    autoTable(doc, {
      startY: ay,
      margin: { left: M, right: M },
      head: [[i18n.t('AsmDashboard.pdfColSeverity'), i18n.t('AsmDashboard.pdfColFindingId'), i18n.t('AsmDashboard.pdfColScope'), i18n.t('AsmDashboard.pdfColOriginalDesc')]],
      body: fpRows,
      columnStyles: {
        0: { cellWidth: 20, fontStyle: 'bold', halign: 'center', fontSize: 7 },
        1: { cellWidth: 42, fontSize: 7 },
        2: { cellWidth: 30, fontSize: 7 },
        3: { cellWidth: W - 20 - 42 - 30, fontSize: 7 },
      },
      headStyles: { fillColor: [100, 116, 139], textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { valign: 'top', lineColor: [229, 231, 235], lineWidth: 0.2, textColor: [107, 114, 128] },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      didParseCell(data) {
        if (data.section === 'body' && data.column.index === 0) {
          const sev = data.cell.raw;
          const [r, g, b] = SEV_RGB[sev] || [107, 114, 128];
          data.cell.styles.textColor = [255, 255, 255];
          data.cell.styles.fillColor = [r, g, b];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
  }

  // ── Footer on every page ─────────────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFillColor(30, 58, 95);
    doc.rect(0, PH - 10, PW, 10, 'F');
    doc.setTextColor(180, 200, 230);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(i18n.t('AsmDashboard.pdfFooter', { domain: domainName }), M, PH - 3.5);
    doc.text(i18n.t('AsmDashboard.pdfPageOf', { page: p, total: pageCount }), PW - M, PH - 3.5, { align: 'right' });
  }

  doc.save(`ASM-${domainName}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function StatusPill({ status, scanning }) {
  const { t } = useTranslation();
  if (scanning) return <span style={pill('#0369a1', '#e0f2fe')}>● {t('AsmDashboard.statusScanning')}</span>;
  if (!status) return <span style={pill('#9ca3af', '#f3f4f6')}>{t('AsmDashboard.statusNoScan')}</span>;
  if (status === 'COMPLETED') return <span style={pill('#16a34a', '#f0fdf4')}>✓ {t('AsmDashboard.statusCompleted')}</span>;
  if (status?.startsWith('FAILED')) return <span style={pill('#dc2626', '#fef2f2')}>✕ {t('AsmDashboard.statusFailed')}</span>;
  if (status?.startsWith('IN_PROGRESS')) {
    const phase = status.replace('IN_PROGRESS: ', '').replace('IN_PROGRESS', '');
    return <span style={pill('#0369a1', '#e0f2fe')}>● {phase || t('AsmDashboard.statusRunning')}</span>;
  }
  return <span style={pill('#6b7280', '#f3f4f6')}>{status}</span>;
}

function SevBadge({ severity }) {
  const s = getSev(severity);
  return <span style={{ background: s.color, color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '2px 7px', borderRadius: 4 }}>{s.label}</span>;
}

function pill(color, bg) {
  return { display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 600, color, background: bg, whiteSpace: 'nowrap' };
}

// ─── Domain Autocomplete ──────────────────────────────────────────────────────
// Replaces <select> for domain lists — shows up to 5 filtered suggestions.
function DomainAutocomplete({ value, onChange, domainNames, placeholder, style = {}, inputStyle = {} }) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('AsmDashboard.searchDomainPlaceholder');
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const selectedName = (domainNames.find(d => String(d.id) === String(value)) || {}).name || '';

  React.useEffect(() => { setQuery(selectedName); }, [selectedName]);

  const suggestions = domainNames
    .filter(d => !query || d.name.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 5);

  const handleChange = (e) => {
    setQuery(e.target.value);
    setOpen(true);
    if (!e.target.value) onChange('');
  };

  const handleFocus = () => { setQuery(''); setOpen(true); };

  const handleBlur = () => {
    setTimeout(() => { setOpen(false); setQuery(selectedName); }, 150);
  };

  const handleSelect = (d) => {
    onChange(String(d.id));
    setQuery(d.name);
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      <input
        value={query}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={resolvedPlaceholder}
        style={{
          width: '100%', padding: '9px 12px', borderRadius: 8,
          border: '1.5px solid #d1d5db', fontSize: '0.875rem',
          background: '#fff', color: '#374151', outline: 'none', boxSizing: 'border-box',
          ...inputStyle,
        }}
      />
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          zIndex: 300, boxShadow: '0 4px 16px rgba(0,0,0,0.14)', overflow: 'hidden',
        }}>
          {suggestions.map(d => (
            <div
              key={d.id}
              onMouseDown={() => handleSelect(d)}
              style={{
                padding: '9px 14px', cursor: 'pointer', fontSize: '0.875rem',
                color: String(d.id) === String(value) ? '#1e40af' : '#374151',
                background: String(d.id) === String(value) ? '#eff6ff' : '#fff',
                fontWeight: String(d.id) === String(value) ? 700 : 400,
                borderBottom: '1px solid #f3f4f6',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { if (String(d.id) !== String(value)) e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={e => { e.currentTarget.style.background = String(d.id) === String(value) ? '#eff6ff' : '#fff'; }}
            >
              🌐 {d.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Scan Quota Banner ────────────────────────────────────────────────────────
function ScanQuotaBanner({ quota, onBuy }) {
  const { t } = useTranslation();
  if (!quota) return null;

  const light  = quota.light  || {};
  const full   = quota.full   || quota;
  const deep   = quota.deep   || {};
  const bundle = quota.bundle || {};

  const lightFree   = light.weeklyFreeLeft  ?? 0;
  const lightPaid   = light.purchased ?? 0;
  const fullFree    = full.weeklyFreeLeft   ?? 0;
  const fullPaid    = full.purchased  ?? 0;
  const deepCredits = deep.purchased  ?? 0;
  const bundleCredits = bundle.credits ?? 0;
  const resetsOn    = full.weeklyResetsOn || light.weeklyResetsOn || '';

  const items = [
    { icon: '⚡', label: t('AsmDashboard.quotaLight'),   free: lightFree, freeOf: light.weeklyFreeTotal ?? 50, paid: lightPaid,    color: '#0369a1', paidOnly: false },
    { icon: '▶',  label: t('AsmDashboard.quotaFull'),    free: fullFree,  freeOf: full.weeklyFreeTotal  ?? 3,  paid: fullPaid,     color: '#16a34a', paidOnly: false },
    { icon: '🔬', label: t('AsmDashboard.quotaDeep'),    free: 0,         freeOf: 0,                           paid: deepCredits,  color: '#7c3aed', paidOnly: true  },
    { icon: '📦', label: t('AsmDashboard.quotaBundles'), free: 0,         freeOf: 0,                           paid: bundleCredits,color: '#d97706', paidOnly: true  },
  ];

  return (
    <div style={{
      display: 'flex', alignItems: 'stretch', gap: 0,
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
      marginBottom: 16, overflow: 'hidden',
    }}>
      {items.map((item, i) => {
        const val = item.paidOnly ? item.paid : item.free;
        const lowColor = item.paidOnly
          ? (item.paid === 0 ? '#dc2626' : item.color)
          : (item.free === 0 ? '#dc2626' : item.free === 1 ? '#d97706' : item.color);
        return (
          <div key={item.label} style={{
            flex: 1, padding: '10px 14px', textAlign: 'center',
            borderRight: i < items.length - 1 ? '1px solid #f1f5f9' : 'none',
            background: val === 0 && !item.paidOnly ? '#fff8f8' : '#fff',
          }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3, whiteSpace: 'nowrap' }}>
              {item.icon} {item.label}
            </div>
            {item.paidOnly ? (
              <div style={{ fontWeight: 700, fontSize: 18, color: lowColor, lineHeight: 1 }}>{item.paid}</div>
            ) : (
              <div style={{ lineHeight: 1 }}>
                <span style={{ fontWeight: 700, fontSize: 18, color: lowColor }}>{item.free}</span>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>/{item.freeOf}</span>
                {item.paid > 0 && (
                  <span style={{ fontSize: 10, color: item.color, marginLeft: 4, fontWeight: 600 }}>+{item.paid}</span>
                )}
              </div>
            )}
            <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>
              {item.paidOnly ? t('AsmDashboard.quotaCredits') : t('AsmDashboard.quotaFreePerWeek')}
            </div>
          </div>
        );
      })}
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', gap: 4, padding: '10px 14px', borderLeft: '1px solid #e2e8f0', flexShrink: 0 }}>
        {resetsOn && <span style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>{t('AsmDashboard.quotaResets', { date: resetsOn })}</span>}
        <button onClick={onBuy} style={{
          padding: '6px 14px', background: '#2563eb', color: '#fff',
          border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
        }}>{t('AsmDashboard.buyScans')}</button>
      </div>
    </div>
  );
}

// ─── Buy Scans Modal ──────────────────────────────────────────────────────────
function BuyScanModal({ onClose }) {
  const { t } = useTranslation();
  const SCAN_PRODUCTS = {
    light: {
      label: t('AsmDashboard.productLightLabel'), icon: '⚡', color: '#0369a1', desc: t('AsmDashboard.productLightDesc'),
      packs: [
        { qty: 50,  price: '$49 CAD',  detail: t('AsmDashboard.productLightPack50'), badge: null },
        { qty: 200, price: '$149 CAD', detail: t('AsmDashboard.productLightPack200'), badge: t('AsmDashboard.badgeBestValue') },
      ],
      endpoint: '/partner/scan-quota/checkout/light', qtyKey: 'packSize',
    },
    full: {
      label: t('AsmDashboard.productFullLabel'), icon: '▶', color: '#16a34a', desc: t('AsmDashboard.productFullDesc'),
      packs: [
        { qty: 10, price: '$79 CAD',  detail: t('AsmDashboard.productFullPack10'), badge: null },
        { qty: 25, price: '$149 CAD', detail: t('AsmDashboard.productFullPack25'), badge: t('AsmDashboard.badgePopular') },
        { qty: 50, price: '$249 CAD', detail: t('AsmDashboard.productFullPack50'), badge: null },
      ],
      endpoint: '/partner/scan-quota/checkout', qtyKey: 'packSize',
    },
    deep: {
      label: t('AsmDashboard.productDeepLabel'), icon: '🔬', color: '#7c3aed', desc: t('AsmDashboard.productDeepDesc'),
      packs: [
        { qty: 5,  price: '$99 CAD',  detail: t('AsmDashboard.productDeepPack5'), badge: null },
        { qty: 15, price: '$249 CAD', detail: t('AsmDashboard.productDeepPack15'), badge: t('AsmDashboard.badgeBestValue') },
      ],
      endpoint: '/partner/scan-quota/checkout/deep', qtyKey: 'packSize',
    },
    bundle: {
      label: t('AsmDashboard.productBundleLabel'), icon: '📦', color: '#d97706', desc: t('AsmDashboard.productBundleDesc'),
      packs: [
        { qty: 1, price: '$149 CAD', detail: t('AsmDashboard.productBundlePack1'), badge: null },
        { qty: 3, price: '$399 CAD', detail: t('AsmDashboard.productBundlePack3'), badge: t('AsmDashboard.badgePopular') },
        { qty: 5, price: '$599 CAD', detail: t('AsmDashboard.productBundlePack5'), badge: t('AsmDashboard.badgeBestValue') },
      ],
      endpoint: '/partner/scan-quota/checkout/bundle', qtyKey: 'bundleCount',
    },
  };
  const [activeTab, setActiveTab] = useState('full');
  const [loading, setLoading] = useState(null);

  const buy = async (product, qty) => {
    const key = `${product.label}-${qty}`;
    setLoading(key);
    try {
      const r = await axiosInstance.post(product.endpoint, { [product.qtyKey]: qty });
      window.location.href = r.data.checkoutUrl;
    } catch (e) {
      Swal.fire(t('AsmDashboard.error'), e.response?.data?.error || t('AsmDashboard.couldNotStartCheckout'), 'error');
      setLoading(null);
    }
  };

  const product = SCAN_PRODUCTS[activeTab];
  const tabs = Object.entries(SCAN_PRODUCTS);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, width: 520, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 0' }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{t('AsmDashboard.purchaseScanCredits')}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, padding: '12px 24px 0', borderBottom: '1px solid #e2e8f0' }}>
          {tabs.map(([key, p]) => (
            <button key={key} onClick={() => setActiveTab(key)} style={{
              padding: '7px 14px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
              borderBottom: activeTab === key ? `2px solid ${p.color}` : '2px solid transparent',
              background: 'none', color: activeTab === key ? p.color : '#6b7280',
              marginBottom: -1,
            }}>
              {p.icon} {p.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: '16px 24px 24px' }}>
          <p style={{ margin: '0 0 16px', color: '#475569', fontSize: 13 }}>{product.desc}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {product.packs.map(p => (
              <div key={p.qty} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                border: `2px solid ${product.color}25`, borderRadius: 10, padding: '13px 16px',
                background: `${product.color}06`, position: 'relative',
              }}>
                {p.badge && (
                  <span style={{
                    position: 'absolute', top: -9, right: 14,
                    background: product.color, color: '#fff', fontSize: 10, fontWeight: 700,
                    padding: '2px 8px', borderRadius: 10,
                  }}>{p.badge}</span>
                )}
                <div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>{p.price}</div>
                  <div style={{ color: '#64748b', fontSize: 12, marginTop: 1 }}>{p.detail}</div>
                </div>
                <button
                  disabled={loading !== null}
                  onClick={() => buy(product, p.qty)}
                  style={{
                    padding: '7px 16px', background: product.color, color: '#fff',
                    border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600,
                    cursor: loading !== null ? 'not-allowed' : 'pointer',
                    opacity: loading !== null ? 0.7 : 1, minWidth: 70, flexShrink: 0,
                  }}>
                  {loading === `${product.label}-${p.qty}` ? '…' : t('AsmDashboard.buy')}
                </button>
              </div>
            ))}
          </div>
          <p style={{ margin: '16px 0 0', color: '#94a3b8', fontSize: 11, textAlign: 'center' }}>
            {t('AsmDashboard.stripeFooter')}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ASMDashboard({ isAdmin = false }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('domains');
  const [domains, setDomains]     = useState([]);     // current page of DomainSummaryDTO
  const [domainNames, setDomainNames]   = useState([]); // lightweight id+name list for dropdowns
  const [domainPaging, setDomainPaging] = useState({ totalElements: 0, totalPages: 0 });
  const [domainStats, setDomainStats]   = useState({ domainCount: 0, subdomainCount: 0, ipCount: 0, openPortCount: 0 });
  const [dPage, setDPage]         = useState(0);
  const [dSearch, setDSearch]     = useState('');
  const [dSearchInput, setDSearchInput] = useState('');
  const [scans,   setScans]       = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [scanningIds, setScanningIds]   = useState(new Set());
  const [scanQuota,  setScanQuota]  = useState(null);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [intelSubTab, setIntelSubTab]   = useState('deep');

  // Findings tab
  const [selectedDomainId, setSelectedDomainId] = useState('');
  const [reportData, setReportData]   = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  // sevFilter, moduleFilter, phaseFilter, deepStatus, findSearch, expandedSubs
  // all live in FindingsTab (extracted to module level to prevent remount on parent re-render)

  const pollRef          = useRef(null);
  const currentPageRef   = useRef(0);
  const currentSearchRef = useRef('');
  const tabRef           = useRef('domains');  // tracks current tab for poll guard
  const scansRef         = useRef([]);
  const scanningIdsRef   = useRef(new Set());
  const quotaAccessRef   = useRef(null);       // null=unknown, 'ok', 'denied'
  const pollFailsRef     = useRef(0);          // consecutive poll failure count
  // Records the ISO timestamp when the user clicked "Scan" for each domain.
  // Used to distinguish the new scan from older completed scans in the same scanExecutions list.
  const scanStartedAtRef = useRef({});

  // ── Fetch partner scan quota (no-op for non-partners — returns 204) ──────────
  const fetchQuota = useCallback(async () => {
    // Platform admins are never partner accounts — skip entirely to avoid 403s.
    if (isAdmin) return;
    // Skip once we know this user has no partner access — avoids repeated 403s.
    if (quotaAccessRef.current === 'denied') return;
    try {
      const r = await axiosInstance.get('/partner/scan-quota/all');
      if (r.status === 204 || !r.data) { setScanQuota(null); return; }
      quotaAccessRef.current = 'ok';
      setScanQuota(r.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        quotaAccessRef.current = 'denied';
        setScanQuota(null);
        return;
      }
      // Fall back to legacy endpoint
      try {
        const r2 = await axiosInstance.get('/partner/scan-quota');
        if (r2.status === 204 || !r2.data) { setScanQuota(null); return; }
        quotaAccessRef.current = 'ok';
        setScanQuota({ full: r2.data });
      } catch (err2) {
        if (err2?.response?.status === 403) quotaAccessRef.current = 'denied';
        setScanQuota(null);
      }
    }
  }, [isAdmin]);

  // ── Data fetchers ────────────────────────────────────────────────────────────
  const fetchScans = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/asm/scans');
      const flatScans = res.data || [];
      setScans(flatScans);

      setScanningIds(prev => {
        if (prev.size === 0) return prev;
        const next = new Set(prev);
        for (const domainId of prev) {
          const triggeredAt = scanStartedAtRef.current[domainId];
          if (!triggeredAt) { next.delete(domainId); continue; }
          const newScan = flatScans.find(s =>
            s.domainId === domainId &&
            new Date(s.startTime) >= new Date(triggeredAt)
          );
          if (newScan && !newScan.status?.startsWith('IN_PROGRESS') && newScan.status !== 'PENDING') {
            next.delete(domainId);
            delete scanStartedAtRef.current[domainId];
          }
        }
        return next;
      });
    } catch { /* silent */ }
  }, []);

  const fetchDomains = useCallback(async (page = 0, search = '') => {
    try {
      const params = new URLSearchParams({ page, size: 10 });
      if (search) params.set('search', search);
      const res = await axiosInstance.get(`/asm/domains?${params}`);
      setDomains(res.data.content || []);
      setDomainPaging({ totalElements: res.data.totalElements || 0, totalPages: res.data.totalPages || 0 });
    } catch { /* silent */ }
  }, []);

  const fetchDomainStats = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/asm/domains/stats');
      setDomainStats(res.data || {});
    } catch { /* silent */ }
  }, []);

  const fetchDomainNames = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/asm/domains/names');
      setDomainNames(res.data || []);
    } catch { /* silent */ }
  }, []);

  const loadReport = useCallback(async (domainId) => {
    if (!domainId) return;
    setLoadingReport(true);
    setReportData(null);
    try {
      const res = await axiosInstance.get(`/asm/reports/domains/${domainId}/attack-surface`);
      setReportData(res.data);
    } catch {
      Swal.fire(t('AsmDashboard.error'), t('AsmDashboard.failedLoadReport'), 'error');
    } finally {
      setLoadingReport(false);
    }
  }, [t]);

  // ── Keep stale-closure refs in sync with state ──────────────────────────────
  useEffect(() => { currentPageRef.current   = dPage;        }, [dPage]);
  useEffect(() => { currentSearchRef.current = dSearch;      }, [dSearch]);
  useEffect(() => { tabRef.current           = tab;          }, [tab]);
  useEffect(() => { scansRef.current         = scans;        }, [scans]);
  useEffect(() => { scanningIdsRef.current   = scanningIds;  }, [scanningIds]);

  // ── Search debounce: typed input → dSearch (300ms) + reset page ─────────────
  useEffect(() => {
    const t = setTimeout(() => { setDSearch(dSearchInput); setDPage(0); }, 300);
    return () => clearTimeout(t);
  }, [dSearchInput]);

  // ── Fetch domain page whenever page or search changes ───────────────────────
  useEffect(() => {
    fetchDomains(dPage, dSearch);
  }, [dPage, dSearch, fetchDomains]);

  // ── Bootstrap ────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchDomainStats();
    fetchDomainNames();
    fetchScans();
    fetchQuota();
    // Show success toast if returning from Stripe checkout
    const params = new URLSearchParams(window.location.search);
    const scanPack = params.get('scan_pack');
    if (scanPack === 'purchased') {
      Swal.fire({ icon: 'success', title: t('AsmDashboard.toastFullAddedTitle'), text: t('AsmDashboard.toastFullAddedText'), timer: 3000, showConfirmButton: false });
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    } else if (scanPack === 'light_purchased') {
      Swal.fire({ icon: 'success', title: t('AsmDashboard.toastLightAddedTitle'), text: t('AsmDashboard.toastLightAddedText'), timer: 3000, showConfirmButton: false });
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    } else if (scanPack === 'deep_purchased') {
      Swal.fire({ icon: 'success', title: t('AsmDashboard.toastDeepAddedTitle'), text: t('AsmDashboard.toastDeepAddedText'), timer: 3000, showConfirmButton: false });
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    } else if (scanPack === 'bundle_purchased') {
      Swal.fire({ icon: 'success', title: t('AsmDashboard.toastBundleAddedTitle'), text: t('AsmDashboard.toastBundleAddedText'), timer: 4000, showConfirmButton: false });
      window.history.replaceState({}, '', window.location.pathname + window.location.hash);
    }
  }, [fetchDomainStats, fetchDomainNames, fetchScans, fetchQuota, t]);

  // ── Polling while any scan is active ────────────────────────────────────────
  // Single stable interval — reads live state via refs so adding scans/scanningIds
  // to the dependency array (which caused the interval to clear+restart on every
  // fetchScans() response) is not needed. fetchDomains is guarded to the domains
  // tab only — no point refreshing the domain list while on Findings/History/etc.
  // Consecutive network failures back off up to 5× (max 40 s effective interval).
  useEffect(() => {
    pollRef.current = setInterval(async () => {
      const hasActive =
        scanningIdsRef.current.size > 0 ||
        scansRef.current.some(s => s.status?.startsWith('IN_PROGRESS'));
      if (!hasActive) { pollFailsRef.current = 0; return; }

      // Back off: skip N ticks when network is down (1 fail → skip 1, 3 fails → skip 4)
      if (pollFailsRef.current > 0) {
        const skipTicks = Math.min(pollFailsRef.current, 4);
        pollFailsRef.current -= 1;
        if (skipTicks > 0) return;
      }

      try {
        await fetchScans();
        if (tabRef.current === 'domains') {
          fetchDomains(currentPageRef.current, currentSearchRef.current);
        }
        pollFailsRef.current = 0;
      } catch {
        pollFailsRef.current = Math.min(pollFailsRef.current + 2, 4);
      }
    }, 8000);
    return () => { clearInterval(pollRef.current); pollRef.current = null; };
  }, [fetchDomains, fetchScans]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const handleAddDomain = async (e) => {
    e.preventDefault();
    const name = newDomain.trim();
    if (!name) return;
    setAddingDomain(true);
    try {
      const addedBy = localStorage.getItem('userName') || localStorage.getItem('username') || 'system';
      await axiosInstance.post('/asm/domains', { name, addedBy });
      setNewDomain('');
      setDPage(0); setDSearch(''); setDSearchInput('');
      await fetchDomains(0, '');
      fetchDomainStats();
      fetchDomainNames();
      Swal.fire({ icon: 'success', title: t('AsmDashboard.toastDomainAdded'), timer: 1500, showConfirmButton: false });
    } catch {
      Swal.fire(t('AsmDashboard.error'), t('AsmDashboard.couldNotAddDomain'), 'error');
    } finally {
      setAddingDomain(false);
    }
  };

  // scanType: 'LIGHT' — phases 0+1 only (marketing recon, ~2-5 min)
  //           'FULL'  — all phases (complete discovery, ~20-60 min)
  // mode: 'INCREMENTAL' — reuse existing data, skip completed phases
  //       'FULL'        — re-run every phase from scratch
  const handleStartScan = async (domainId, scanType = 'FULL', mode = 'INCREMENTAL') => {
    scanStartedAtRef.current[domainId] = new Date().toISOString();
    setScanningIds(prev => new Set([...prev, domainId]));
    const triggeredBy = localStorage.getItem('userName') || localStorage.getItem('username') || 'user';
    try {
      await axiosInstance.post(`/asm/domains/${domainId}/scan`, { scanType, mode, triggeredBy });
      const labels = { LIGHT: t('AsmDashboard.scanLabelLight'), FULL: mode === 'FULL' ? t('AsmDashboard.scanLabelFullRerun') : t('AsmDashboard.scanLabelFull') };
      Swal.fire({ icon: 'info', title: t('AsmDashboard.toastScanStarted', { label: labels[scanType] || t('AsmDashboard.scanLabelGeneric', { type: scanType }) }), text: t('AsmDashboard.statusUpdatesEvery5s'), timer: 2000, showConfirmButton: false });
      fetchDomains(currentPageRef.current, currentSearchRef.current);
      fetchScans();
      fetchQuota();
    } catch (e) {
      delete scanStartedAtRef.current[domainId];
      setScanningIds(prev => { const next = new Set(prev); next.delete(domainId); return next; });
      if (e.response?.status === 429) {
        setShowBuyModal(true);
      } else {
        Swal.fire(t('AsmDashboard.error'), t('AsmDashboard.failedStartScan'), 'error');
      }
    }
  };

  const handleDeleteDomain = async (domainId, name) => {
    const { isConfirmed } = await Swal.fire({
      title: t('AsmDashboard.deleteDomainConfirmTitle', { name }),
      text: t('AsmDashboard.deleteDomainConfirmText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: t('AsmDashboard.delete'),
    });
    if (!isConfirmed) return;
    try {
      await axiosInstance.delete(`/asm/domains/${domainId}`);
      if (selectedDomainId === String(domainId)) { setSelectedDomainId(''); setReportData(null); }
      await fetchDomains(currentPageRef.current, currentSearchRef.current);
      fetchDomainStats();
      fetchDomainNames();
      Swal.fire({ icon: 'success', title: t('AsmDashboard.toastDeleted'), timer: 1500, showConfirmButton: false });
    } catch {
      Swal.fire(t('AsmDashboard.error'), t('AsmDashboard.failedDeleteDomain'), 'error');
    }
  };

  const handleViewFindings = async (domain) => {
    setSelectedDomainId(String(domain.id));
    setTab('findings');
    await loadReport(domain.id);
  };

  // ── Computed ─────────────────────────────────────────────────────────────────
  const stats = {
    domainCount:    domainStats.domainCount    || domainPaging.totalElements || 0,
    subdomainCount: domainStats.subdomainCount || 0,
    ipCount:        domainStats.ipCount        || 0,
    portCount:      domainStats.openPortCount  || 0,
  };
  const activeScans   = scans.filter(s => s.status?.startsWith('IN_PROGRESS')).length;
  const completedScans = scans.filter(s => s.status === 'COMPLETED').length;

  const latestScanFor = (domainId) =>
    scans.find(s => s.domainId === domainId);

  const isScanningDomain = (domainId) =>
    scanningIds.has(domainId) || latestScanFor(domainId)?.status?.startsWith('IN_PROGRESS');

  const scanStagesFor = (domainId) => {
    const ds = scans.filter(s => s.domainId === domainId);
    return {
      light: ds.some(s => (s.scanType || 'FULL').toUpperCase() === 'LIGHT' && s.status === 'COMPLETED'),
      full:  ds.some(s => (s.scanType || 'FULL').toUpperCase() === 'FULL'  && s.status === 'COMPLETED'),
      deep:  ds.some(s => (s.scanType || 'FULL').toUpperCase() === 'DEEP'  && s.status === 'COMPLETED'),
    };
  };

  const nextStepFor = (stages) => {
    if (!stages.light && !stages.full) return { text: t('AsmDashboard.nextStepStart'), color: '#0369a1' };
    if (!stages.full)                  return { text: t('AsmDashboard.nextStepFull'), color: '#16a34a' };
    if (!stages.deep)                  return { text: t('AsmDashboard.nextStepDeep'), color: '#7c3aed' };
    return { text: t('AsmDashboard.nextStepRedTeam'), color: '#d97706' };
  };

  // ── Red Team Intelligence state ──────────────────────────────────────────────
  const [pocDomainId,   setPocDomainId]   = useState('');
  const [pocSeverities, setPocSeverities] = useState(['CRITICAL', 'HIGH', 'MEDIUM']);
  const [pocResults,    setPocResults]    = useState([]);
  const [pocLoading,    setPocLoading]    = useState(false);
  const [pocExpanded,   setPocExpanded]   = useState({});

  // ── Advanced Scan state ──────────────────────────────────────────────────────
  const [advDomainId,  setAdvDomainId]  = useState('');
  const [advModules,   setAdvModules]   = useState(['GIT_RECON','ENV_SECRETS','DEFAULT_CREDS','ACTUATOR','CORS','GHOSTCAT','S3_WRITE']);
  const [advTargets,   setAdvTargets]   = useState(null);
  const [advTargetLoading, setAdvTargetLoading] = useState(false);
  const [advRunning,   setAdvRunning]   = useState(false);

  // ── CWE Risk Bridge state ────────────────────────────────────────────────────
  const [riskBridge,        setRiskBridge]        = useState(null);
  const [riskBridgeLoading, setRiskBridgeLoading] = useState(false);

  const handleRunSimulation = async () => {
    if (!pocDomainId) return;
    setPocLoading(true);
    setPocResults([]);
    setPocExpanded({});
    try {
      const res = await axiosInstance.post(
        `/asm/domains/${pocDomainId}/adversary-simulation`,
        { severities: pocSeverities, limit: 40 }
      );
      setPocResults(res.data || []);
    } catch (e) {
      Swal.fire(t('AsmDashboard.error'), t('AsmDashboard.simulationFailed', { error: e.response?.data || e.message }), 'error');
    } finally {
      setPocLoading(false);
    }
  };

  const loadRiskBridge = useCallback(async () => {
    if (riskBridge !== null) return;
    setRiskBridgeLoading(true);
    try {
      const res = await axiosInstance.get('/cwe/risk-bridge');
      setRiskBridge(res.data || []);
    } catch {
      setRiskBridge([]);
    } finally {
      setRiskBridgeLoading(false);
    }
  }, [riskBridge]);

  const handleLoadAdvTargets = useCallback(async (domainId) => {
    if (!domainId) { setAdvTargets(null); return; }
    setAdvTargetLoading(true);
    setAdvTargets(null);
    try {
      const res = await axiosInstance.get(`/asm/domains/${domainId}/advanced-scan/targets`);
      setAdvTargets(res.data);
    } catch {
      setAdvTargets({ urls: [], ips: [], bucketUrls: [] });
    } finally {
      setAdvTargetLoading(false);
    }
  }, []);

  const handleStartAdvancedScan = async () => {
    if (!advDomainId || advModules.length === 0) return;
    setAdvRunning(true);
    const triggeredBy = localStorage.getItem('userName') || localStorage.getItem('username') || 'user';
    try {
      await axiosInstance.post(`/asm/domains/${advDomainId}/advanced-scan`, { modules: advModules, triggeredBy });
      // Mark domain as scanning so the polling loop picks it up
      scanStartedAtRef.current[Number(advDomainId)] = new Date().toISOString();
      setScanningIds(prev => new Set([...prev, Number(advDomainId)]));
      Swal.fire({ icon: 'info', title: t('AsmDashboard.toastAdvancedStartedTitle'), text: t('AsmDashboard.toastAdvancedStartedText'), timer: 3000, showConfirmButton: false });
      fetchDomains();
      fetchScans();
      fetchQuota();
    } catch (e) {
      if (e.response?.status === 429) {
        setShowBuyModal(true);
      } else {
        Swal.fire(t('AsmDashboard.error'), t('AsmDashboard.failedStartAdvanced', { error: e.response?.data?.error || e.message }), 'error');
      }
    } finally {
      setAdvRunning(false);
    }
  };

  const toggleAdvModule = (id) => {
    setAdvModules(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };

  // ── Tab definitions ──────────────────────────────────────────────────────────
  const tabs = [
    { id: 'domains',      label: t('AsmDashboard.tabDomains', { count: stats.domainCount }) },
    { id: 'findings',     label: t('AsmDashboard.tabFindings') },
    { id: 'intelligence', label: t('AsmDashboard.tabIntelligence') },
    { id: 'history',      label: t('AsmDashboard.tabHistory', { count: historyTotal || scans.length }) },
    { id: 'risk',         label: t('AsmDashboard.tabRiskBridge') },
  ];

  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div style={S.page}>

      {showBuyModal && <BuyScanModal onClose={() => setShowBuyModal(false)} />}

      {/* ── Header ── */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}>{t('AsmDashboard.pageTitle')}</h1>
          <p style={S.subtitle}>{t('AsmDashboard.pageSubtitle')}</p>
        </div>
        <form onSubmit={handleAddDomain} style={S.addForm}>
          <input
            type="text"
            placeholder={t('AsmDashboard.domainPlaceholder')}
            value={newDomain}
            onChange={e => setNewDomain(e.target.value)}
            style={S.domainInput}
            disabled={addingDomain}
          />
          <button type="submit" disabled={addingDomain} style={S.addBtn}>
            {addingDomain ? '…' : t('AsmDashboard.addDomain')}
          </button>
        </form>
      </div>

      <ScanQuotaBanner quota={scanQuota} onBuy={() => setShowBuyModal(true)} />

      {/* ── Tab Bar ── */}
      <div style={S.tabBar}>
        {tabs.map(t => (
          <button key={t.id} style={{ ...S.tab, ...(tab === t.id ? S.tabActive : {}) }} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      <div>
        {tab === 'domains'      && (
          <DomainsTab
            domainPaging={domainPaging}
            stats={stats}
            completedScans={completedScans}
            activeScans={activeScans}
            scanQuota={scanQuota}
            dSearchInput={dSearchInput}
            setDSearchInput={setDSearchInput}
            dSearch={dSearch}
            dPage={dPage}
            setDPage={setDPage}
            domains={domains}
            latestScanFor={latestScanFor}
            isScanningDomain={isScanningDomain}
            scanStagesFor={scanStagesFor}
            nextStepFor={nextStepFor}
            handleViewFindings={handleViewFindings}
            isAdmin={isAdmin}
            handleStartScan={handleStartScan}
            setAdvDomainId={setAdvDomainId}
            setPocDomainId={setPocDomainId}
            handleLoadAdvTargets={handleLoadAdvTargets}
            setPocResults={setPocResults}
            setTab={setTab}
            setIntelSubTab={setIntelSubTab}
            handleDeleteDomain={handleDeleteDomain}
          />
        )}
        {tab === 'findings'     && (
          <FindingsTab
            key={selectedDomainId}
            selectedDomainId={selectedDomainId}
            setSelectedDomainId={setSelectedDomainId}
            domainNames={domainNames}
            reportData={reportData}
            loadingReport={loadingReport}
            loadReport={loadReport}
            scans={scans}
            scanningIds={scanningIds}
            isAdmin={isAdmin}
          />
        )}
        {tab === 'intelligence' && <IntelligenceTab />}
        {tab === 'history'      && <HistoryTab setHistoryTotal={setHistoryTotal} />}
        {tab === 'risk'         && <CweRiskBridgeTab />}
      </div>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════════
  // TAB: DOMAINS  (replaces Overview + old Domains)
  // ══════════════════════════════════════════════════════════════════════════════

  // FindingsTab is defined at module level (below AsmDashboard) to prevent
  // React from unmounting/remounting it on every parent re-render.

  // ══════════════════════════════════════════════════════════════════════════════
  // TAB: INTELLIGENCE  (Deep Scan + Red Team Intel unified)
  // ══════════════════════════════════════════════════════════════════════════════
  function IntelligenceTab() {
    const ADV_MODULES = [
      { id: 'GIT_RECON',     icon: '📁', label: t('AsmDashboard.advGitReconLabel'),     desc: t('AsmDashboard.advGitReconDesc'),     targets: 'urls',       mitre: 'T1552.001 · T1213.003' },
      { id: 'ENV_SECRETS',   icon: '🔑', label: t('AsmDashboard.advEnvSecretsLabel'),   desc: t('AsmDashboard.advEnvSecretsDesc'),   targets: 'urls',       mitre: 'T1552.001' },
      { id: 'DEFAULT_CREDS', icon: '🔓', label: t('AsmDashboard.advDefaultCredsLabel'), desc: t('AsmDashboard.advDefaultCredsDesc'), targets: 'urls',       mitre: 'T1078.001 · T1110.001' },
      { id: 'ACTUATOR',      icon: '⚙️', label: t('AsmDashboard.advActuatorLabel'),     desc: t('AsmDashboard.advActuatorDesc'),     targets: 'urls',       mitre: 'T1552.002' },
      { id: 'CORS',          icon: '🌐', label: t('AsmDashboard.advCorsLabel'),         desc: t('AsmDashboard.advCorsDesc'),         targets: 'urls',       mitre: 'T1185 · T1539' },
      { id: 'GHOSTCAT',      icon: '👻', label: t('AsmDashboard.advGhostcatLabel'),     desc: t('AsmDashboard.advGhostcatDesc'),     targets: 'ips',        mitre: 'T1190' },
      { id: 'S3_WRITE',      icon: '🪣', label: t('AsmDashboard.advS3WriteLabel'),      desc: t('AsmDashboard.advS3WriteDesc'),      targets: 'bucketUrls', mitre: 'T1608.001 · T1530' },
    ];

    const sharedDomainId = advDomainId || pocDomainId;
    const handleSharedDomainChange = (id) => {
      setAdvDomainId(id); setPocDomainId(id); setPocResults([]);
      id ? handleLoadAdvTargets(id) : setAdvTargets(null);
    };
    const selectedIntelDomain = domains.find(d => String(d.id) === String(sharedDomainId));
    const intelStages = sharedDomainId ? scanStagesFor(Number(sharedDomainId)) : null;

    const targetCount = advTargets
      ? (advTargets.urls?.length || 0) + (advTargets.ips?.length || 0) + (advTargets.bucketUrls?.length || 0)
      : 0;
    const hasTargets = targetCount > 0;
    const targetLabel = { urls: t('AsmDashboard.targetWebUrl'), ips: t('AsmDashboard.targetIpAddress'), bucketUrls: t('AsmDashboard.targetS3Bucket') };
    const targetColor = { urls: '#2563eb', ips: '#7c3aed', bucketUrls: '#d97706' };
    const allSelected  = advModules.length === ADV_MODULES.length;
    const noneSelected = advModules.length === 0;

    // ── Red Team vars ──
    const SEV_OPTS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
    const grouped = pocResults.reduce((acc, r) => {
      const s = (r.severity || 'INFO').toUpperCase();
      (acc[s] = acc[s] || []).push(r);
      return acc;
    }, {});
    const ORDER = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];

    return (
      <div style={{ maxWidth: 920 }}>

        {/* ── Header band with shared domain selector ── */}
        <div style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)', borderRadius: 14, padding: '22px 26px', marginBottom: 20, boxShadow: '0 4px 16px rgba(49,46,129,0.25)' }}>
          <div style={{ fontWeight: 800, fontSize: 18, color: '#fff', marginBottom: 4 }}>{t('AsmDashboard.intelCenterTitle')}</div>
          <div style={{ color: '#a5b4fc', fontSize: 13, marginBottom: 16 }}>
            {t('AsmDashboard.intelCenterSubtitle')}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <DomainAutocomplete
              value={sharedDomainId}
              onChange={id => handleSharedDomainChange(id)}
              domainNames={domainNames}
              placeholder={t('AsmDashboard.searchDomainPlaceholder')}
              style={{ flex: '1 1 260px', maxWidth: 340 }}
              inputStyle={{ background: 'rgba(255,255,255,0.12)', border: '1.5px solid rgba(255,255,255,0.25)', color: '#fff' }}
            />
            {advTargetLoading && <span style={{ color: '#a5b4fc', fontSize: 13 }}>{t('AsmDashboard.loadingTargets')}</span>}
            {sharedDomainId && intelStages && !intelStages.full && (
              <div style={{ background: 'rgba(251,191,36,0.2)', border: '1px solid rgba(251,191,36,0.4)', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: '#fbbf24' }}>
                {t('AsmDashboard.runFullFirstWarning')}
              </div>
            )}
          </div>
          {/* Prerequisite pipeline if domain is selected */}
          {sharedDomainId && intelStages && (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
              {[
                { id: 'light', icon: '⚡', label: t('AsmDashboard.stageLight'),    color: '#818cf8' },
                { id: 'full',  icon: '▶',  label: t('AsmDashboard.stageFull'),     color: '#34d399' },
                { id: 'deep',  icon: '🔬', label: t('AsmDashboard.stageDeep'),     color: '#c084fc' },
              ].map((st, si) => {
                const done = intelStages[st.id];
                return (
                  <React.Fragment key={st.id}>
                    {si > 0 && <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>›</span>}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: done ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.05)', color: done ? st.color : 'rgba(255,255,255,0.35)', border: `1px solid ${done ? st.color + '50' : 'rgba(255,255,255,0.1)'}` }}>
                      {st.icon} {st.label} {done ? '✓' : '○'}
                    </span>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Sub-tab switcher ── */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f1f5f9', borderRadius: 10, padding: 4 }}>
          {[
            { id: 'deep',    label: t('AsmDashboard.subTabDeepLabel'),    desc: t('AsmDashboard.subTabDeepDesc') },
            { id: 'redteam', label: t('AsmDashboard.subTabRedTeamLabel'), desc: t('AsmDashboard.subTabRedTeamDesc') },
          ].map(st => (
            <button key={st.id} onClick={() => setIntelSubTab(st.id)} style={{
              flex: 1, padding: '10px 16px', border: 'none', borderRadius: 8, cursor: 'pointer',
              background: intelSubTab === st.id ? '#fff' : 'transparent',
              color: intelSubTab === st.id ? '#0f172a' : '#64748b',
              fontWeight: intelSubTab === st.id ? 700 : 500, fontSize: 13,
              boxShadow: intelSubTab === st.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.15s', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            }}>
              <span>{st.label}</span>
              <span style={{ fontSize: 11, color: intelSubTab === st.id ? '#64748b' : '#94a3b8', fontWeight: 400 }}>{st.desc}</span>
            </button>
          ))}
        </div>

        {/* ══ DEEP SCAN SECTION ══ */}
        {intelSubTab === 'deep' && (
          <div>
            {/* Target preview */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>
                {t('AsmDashboard.discoveredTargets')}
              </div>
              {!sharedDomainId && (
                <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>{t('AsmDashboard.selectDomainForTargets')}</p>
              )}

              {sharedDomainId && advTargets && (
                hasTargets ? (
                  <>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8, fontWeight: 600 }}>
                      {t('AsmDashboard.extractedFromFullScan', { count: targetCount })}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {['urls', 'ips', 'bucketUrls'].map(type => {
                        const list = advTargets[type] || [];
                        if (!list.length) return null;
                        return (
                          <div key={type}>
                            <span style={{ display: 'inline-block', background: targetColor[type] + '18', color: targetColor[type], border: `1px solid ${targetColor[type]}40`, borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700, marginBottom: 6 }}>
                              {targetLabel[type]} ({list.length})
                            </span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {list.map((t, i) => (
                                <code key={i} style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 5, padding: '2px 8px', fontSize: 11, color: '#334155' }}>{t}</code>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
                    {t('AsmDashboard.noTargetsWarning')}
                  </div>
                )
              )}
            </div>

            {/* Module selector */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  {t('AsmDashboard.selectModules')}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setAdvModules(ADV_MODULES.map(m => m.id))} style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: allSelected ? '#f1f5f9' : '#fff', cursor: 'pointer', color: '#475569' }}>{t('AsmDashboard.all')}</button>
                  <button onClick={() => setAdvModules([])} style={{ fontSize: 11, padding: '3px 10px', border: '1px solid #cbd5e1', borderRadius: 6, background: noneSelected ? '#f1f5f9' : '#fff', cursor: 'pointer', color: '#475569' }}>{t('AsmDashboard.none')}</button>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {ADV_MODULES.map(mod => {
                  const active = advModules.includes(mod.id);
                  const tlist  = advTargets?.[mod.targets] || [];
                  const hasT   = tlist.length > 0;
                  return (
                    <label key={mod.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', padding: '12px 14px', borderRadius: 10, border: `1.5px solid ${active ? targetColor[mod.targets] + '50' : '#e2e8f0'}`, background: active ? targetColor[mod.targets] + '06' : '#fafafa', transition: 'all 0.15s' }}>
                      <input type="checkbox" checked={active} onChange={() => toggleAdvModule(mod.id)} style={{ marginTop: 3, accentColor: targetColor[mod.targets], width: 16, height: 16, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 15 }}>{mod.icon}</span>
                          <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{mod.label}</span>
                          {advTargets && (
                            <span style={{ fontSize: 11, color: hasT ? targetColor[mod.targets] : '#9ca3af', background: hasT ? targetColor[mod.targets] + '12' : '#f1f5f9', border: `1px solid ${hasT ? targetColor[mod.targets] + '30' : '#e5e7eb'}`, borderRadius: 5, padding: '1px 7px', fontWeight: 600 }}>
                              {hasT ? t('AsmDashboard.targetsCount', { count: tlist.length }) : t('AsmDashboard.noTargets')}
                            </span>
                          )}
                          <span style={{ marginLeft: 'auto', fontSize: 10, color: '#a78bfa', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 4, padding: '1px 6px', fontWeight: 600, whiteSpace: 'nowrap' }}>{mod.mitre}</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 3, lineHeight: 1.5 }}>{mod.desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Launch */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                <b>{advModules.length}</b> {t('AsmDashboard.modulesSelected', { count: advModules.length })}
                {advDomainId && advTargets ? ` · ${t('AsmDashboard.targetsQueued', { count: targetCount })}` : ''}
                {' · '}{t('AsmDashboard.resultsAddedToFindings')}
              </div>
              <button
                disabled={!advDomainId || advModules.length === 0 || !hasTargets || advRunning}
                onClick={handleStartAdvancedScan}
                style={{ padding: '10px 28px', borderRadius: 9, border: 'none', fontSize: 14, fontWeight: 700, background: (!advDomainId || advModules.length === 0 || !hasTargets) ? '#e2e8f0' : '#1e40af', color: (!advDomainId || advModules.length === 0 || !hasTargets) ? '#94a3b8' : '#fff', cursor: (!advDomainId || advModules.length === 0 || !hasTargets || advRunning) ? 'not-allowed' : 'pointer', transition: 'background 0.15s' }}
              >{advRunning ? t('AsmDashboard.startingDeepScan') : t('AsmDashboard.runDeepScan')}</button>
            </div>

            {advDomainId && isScanningDomain(Number(advDomainId)) && (
              <div style={{ marginTop: 14, padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 9, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#1e40af' }}>
                <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>
                {t('AsmDashboard.deepScanRunningFor')} <b>{selectedIntelDomain?.name}</b>{t('AsmDashboard.deepScanRunningSuffix')}
              </div>
            )}
          </div>
        )}

        {/* ══ RED TEAM SECTION ══ */}
        {intelSubTab === 'redteam' && (
          <div style={{ fontFamily: 'Inter, sans-serif' }}>
            {/* Controls */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '18px 24px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>{t('AsmDashboard.severityScope')}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {SEV_OPTS.map(s => {
                    const active = pocSeverities.includes(s);
                    const sv     = getSev(s);
                    return (
                      <button key={s} onClick={() => setPocSeverities(prev => active ? prev.filter(x => x !== s) : [...prev, s])}
                        style={{ padding: '5px 12px', borderRadius: 20, border: `1.5px solid ${active ? sv.color : '#e5e7eb'}`, background: active ? sv.color : '#fff', color: active ? '#fff' : '#6b7280', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}
                      >{s}</button>
                    );
                  })}
                </div>
              </div>
              <button
                disabled={!pocDomainId || pocLoading || pocSeverities.length === 0}
                onClick={handleRunSimulation}
                style={{ ...RT.runBtn, opacity: (!pocDomainId || pocLoading || pocSeverities.length === 0) ? 0.5 : 1, cursor: (!pocDomainId || pocLoading || pocSeverities.length === 0) ? 'not-allowed' : 'pointer' }}
              >
                {pocLoading ? <><span style={RT.spin}>⟳</span> {t('AsmDashboard.simulating')}</> : t('AsmDashboard.runSimulation')}
              </button>
            </div>

        {/* ── Loading state ── */}
        {pocLoading && (
          <div style={RT.loadingBox}>
            <div style={{ fontSize: '2.5rem', marginBottom: 12, animation: 'spin 1s linear infinite' }}>⟳</div>
            <div style={{ fontWeight: 700, color: '#1e3a5f', marginBottom: 6 }}>{t('AsmDashboard.runningSimulation')}</div>
            <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>
              {t('AsmDashboard.simulationProbing', { target: selectedIntelDomain?.name || t('AsmDashboard.target') })}
            </div>
          </div>
        )}

        {/* ── Empty state ── */}
        {!pocLoading && pocResults.length === 0 && pocDomainId && (
          <div style={RT.emptyBox}>
            <div style={{ fontSize: '2.5rem', marginBottom: 10 }}>🛡️</div>
            <div style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>{t('AsmDashboard.noResultsYet')}</div>
            <div style={{ fontSize: '0.82rem', color: '#9ca3af' }}>
              {t('AsmDashboard.noResultsHint')}
            </div>
          </div>
        )}
        {!pocLoading && !pocDomainId && (
          <div style={RT.emptyBox}>
            <div style={{ fontSize: '2.5rem', marginBottom: 10 }}>🎯</div>
            <div style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>{t('AsmDashboard.selectTargetDomain')}</div>
            <div style={{ fontSize: '0.82rem', color: '#9ca3af' }}>
              {t('AsmDashboard.selectTargetHint')}
            </div>
          </div>
        )}

        {/* ── Summary strip ── */}
        {!pocLoading && pocResults.length > 0 && (
          <div style={RT.summaryStrip}>
            <div style={RT.summaryLeft}>
              <span style={RT.domainChip}>🌐 {selectedIntelDomain?.name || '—'}</span>
              <span style={RT.simLabel}>{t('AsmDashboard.scenariosSimulated', { count: pocResults.length })}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {ORDER.map(s => {
                const n = (grouped[s] || []).length;
                if (!n) return null;
                return (
                  <div key={s} style={{ ...RT.sevBubble, background: getSev(s).bg, border: `1.5px solid ${getSev(s).border}` }}>
                    <span style={{ fontWeight: 800, fontSize: '1.1rem', color: getSev(s).color }}>{n}</span>
                    <span style={{ fontSize: '0.65rem', color: getSev(s).color, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── PoC Cards ── */}
        {!pocLoading && ORDER.map(sev => {
          const items = grouped[sev] || [];
          if (!items.length) return null;
          const sv = getSev(sev);
          return (
            <div key={sev} style={{ marginBottom: 24 }}>
              <div style={{ ...RT.sevGroupHeader, borderLeft: `4px solid ${sv.color}` }}>
                <span style={{ ...RT.sevDot, background: sv.color }} />
                <span style={{ fontWeight: 700, color: sv.color, fontSize: '0.88rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{sev}</span>
                <span style={{ color: '#9ca3af', fontSize: '0.78rem' }}>{t('AsmDashboard.scenarioCount', { count: items.length })}</span>
              </div>
              {items.map((poc, idx) => {
                const key  = `${sev}-${idx}`;
                const open = pocExpanded[key];
                return (
                  <div key={key} style={{ ...RT.pocCard, borderLeft: `4px solid ${sv.color}` }}>

                    {/* Card header — always visible */}
                    <div
                      style={RT.pocHeader}
                      onClick={() => setPocExpanded(p => ({ ...p, [key]: !p[key] }))}
                    >
                      <div style={RT.pocHeaderLeft}>
                        <span style={{ ...RT.sevBadge, background: sv.color }}>{sev}</span>
                        <span style={RT.pocTemplateId}>{poc.templateId || '—'}</span>
                        <span style={RT.pocSubdomain}>{poc.subdomainName || poc.domain}</span>
                      </div>
                      <div style={RT.pocHeaderRight}>
                        {poc.mitreTechnique && (
                          <span style={RT.mitreChip}>{poc.mitreTechnique?.split(' — ')[0]}</span>
                        )}
                        <span style={RT.chevronIcon}>{open ? '▾' : '▸'}</span>
                      </div>
                    </div>

                    {/* Adversary narrative — always visible */}
                    <div style={RT.narrative}>
                      {poc.adversaryNarrative || '—'}
                    </div>

                    {/* Expanded detail */}
                    {open && (
                      <div style={RT.pocDetail}>

                        {/* Impact */}
                        {poc.impactSummary && (
                          <div style={RT.impactRow}>
                            <span style={RT.impactLabel}>{t('AsmDashboard.impact')}</span>
                            <span style={RT.impactText}>{poc.impactSummary}</span>
                          </div>
                        )}

                        {/* Attack scenario */}
                        {poc.attackScenario && (
                          <div style={{ marginBottom: 14 }}>
                            <div style={RT.sectionLabel}>{t('AsmDashboard.attackScenario')}</div>
                            <div style={RT.scenarioBox}>
                              {poc.attackScenario.split('\n').map((line, i) => (
                                <div key={i} style={RT.scenarioLine}>{line}</div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* PoC Command */}
                        {poc.pocCommand && (
                          <div style={{ marginBottom: 14 }}>
                            <div style={RT.sectionLabel}>{t('AsmDashboard.pocCommand')}</div>
                            <div style={RT.terminal}>
                              <div style={RT.terminalDots}>
                                <span style={{ ...RT.dot3, background: '#ff5f57' }} />
                                <span style={{ ...RT.dot3, background: '#febc2e' }} />
                                <span style={{ ...RT.dot3, background: '#28c840' }} />
                                <span style={{ marginLeft: 8, color: '#6b7280', fontSize: '0.7rem' }}>cataam@redteam ~ $</span>
                              </div>
                              <pre style={RT.terminalCode}>{poc.pocCommand}</pre>
                            </div>
                          </div>
                        )}

                        {/* Evidence Output */}
                        {poc.pocOutput && poc.pocOutput.trim() !== '(no output)' && (
                          <div style={{ marginBottom: 14 }}>
                            <div style={RT.sectionLabel}>{t('AsmDashboard.evidenceCapture')}</div>
                            <div style={RT.evidenceBox}>
                              <pre style={RT.evidencePre}>{poc.pocOutput}</pre>
                            </div>
                          </div>
                        )}

                        {/* Extracted Evidence Artifacts */}
                        {poc.extractedEvidence && poc.extractedEvidence.length > 0 && (
                          <div style={{ marginBottom: 14 }}>
                            <div style={RT.sectionLabel}>
                              {t('AsmDashboard.extractedArtifacts', { count: poc.extractedEvidence.length })}
                            </div>
                            <div style={RT.artifactTable}>
                              {poc.extractedEvidence.map((ev, ei) => (
                                <div key={ei} style={{
                                  ...RT.artifactRow,
                                  background: ei % 2 === 0 ? '#0f172a' : '#111827',
                                }}>
                                  <div style={RT.artifactType}>
                                    <span style={RT.artifactTypeBadge}>{ev.type}</span>
                                  </div>
                                  <div style={RT.artifactContent}>
                                    <div style={RT.artifactValue}>{ev.value || ev.rawValue}</div>
                                    <div style={RT.artifactMeta}>
                                      <span style={RT.artifactLoc}>{ev.location}</span>
                                      {ev.context && (
                                        <span style={RT.artifactCtx}> — {ev.context}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* MITRE + Remediation */}
                        <div style={RT.bottomRow}>
                          {poc.mitreTechnique && (
                            <div style={RT.mitreRow}>
                              <span style={RT.mitreLabel}>{t('AsmDashboard.mitreAttack')}</span>
                              <span style={RT.mitreValue}>{poc.mitreTechnique}</span>
                            </div>
                          )}
                          {poc.remediationHint && (
                            <div style={RT.remediationRow}>
                              <span style={RT.remLabel}>{t('AsmDashboard.remediation')}</span>
                              <span style={RT.remText}>{poc.remediationHint}</span>
                            </div>
                          )}
                          {poc.evidenceTimestamp && (
                            <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 4 }}>
                              {t('AsmDashboard.simulatedAt', { date: new Date(poc.evidenceTimestamp + (poc.evidenceTimestamp.endsWith('Z') ? '' : 'Z')).toLocaleString() })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
          </div>
        )}

      </div>
    );
  }

  // HistoryTab is defined at module level (below AsmDashboard) — see below.

  // ══════════════════════════════════════════════════════════════════════════════
  // TAB: CWE RISK BRIDGE
  // ══════════════════════════════════════════════════════════════════════════════
  function CweRiskBridgeTab() {
    useEffect(() => { loadRiskBridge(); }, []);

    const RISK_COLOR = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#d97706', LOW: '#16a34a' };
    const RISK_BG    = { CRITICAL: '#fef2f2', HIGH: '#fff7ed', MEDIUM: '#fffbeb', LOW: '#f0fdf4' };

    if (riskBridgeLoading) {
      return (
        <div style={{ background: '#fff', borderRadius: 12, padding: '60px 20px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: 12 }}>⏳</div>
          <p style={{ color: '#6b7280', margin: 0 }}>{t('AsmDashboard.correlatingFindings')}</p>
        </div>
      );
    }

    if (!riskBridge || riskBridge.length === 0) {
      return (
        <div style={S.empty}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>🔗</div>
          <p style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>{t('AsmDashboard.noCweOverlaps')}</p>
          <p style={{ fontSize: '0.82rem', color: '#9ca3af', margin: 0 }}>
            {t('AsmDashboard.noCweOverlapsHint')}
          </p>
        </div>
      );
    }

    const byCritical = riskBridge.filter(r => r.riskLevel === 'CRITICAL').length;
    const byHigh     = riskBridge.filter(r => r.riskLevel === 'HIGH').length;

    return (
      <div style={{ fontFamily: 'Inter, sans-serif' }}>
        {/* ── Header banner ── */}
        <div style={{ background: 'linear-gradient(135deg,#1e1b4b 0%,#312e81 100%)', borderRadius: 14, padding: '20px 26px', marginBottom: 24, boxShadow: '0 4px 16px rgba(49,46,129,0.3)' }}>
          <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#fff', marginBottom: 6 }}>{t('AsmDashboard.cweRiskBridgeTitle')}</div>
          <div style={{ fontSize: '0.78rem', color: '#a5b4fc', lineHeight: 1.5 }}>
            {t('AsmDashboard.cweRiskBridgeIntro')}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
            {[
              { label: t('AsmDashboard.atRiskControls'), value: riskBridge.length, color: '#c7d2fe' },
              { label: t('AsmDashboard.critical'),       value: byCritical,         color: '#fca5a5' },
              { label: t('AsmDashboard.high'),           value: byHigh,             color: '#fdba74' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: '8px 16px', textAlign: 'center', minWidth: 80 }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: '0.68rem', color: '#a5b4fc', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Control cards ── */}
        {riskBridge.map((item, idx) => {
          const rc = RISK_COLOR[item.riskLevel] || '#6b7280';
          const rb = RISK_BG[item.riskLevel]   || '#f9fafb';
          const auditColor = item.auditStatus === 'PASS' ? '#16a34a' : item.auditStatus === 'FAIL' ? '#dc2626' : '#6b7280';
          const auditBg    = item.auditStatus === 'PASS' ? '#dcfce7' : item.auditStatus === 'FAIL' ? '#fee2e2' : '#f3f4f6';
          return (
            <div key={idx} style={{ background: '#fff', borderRadius: 10, marginBottom: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid ${rb}`, borderLeft: `4px solid ${rc}` }}>
              <div style={{ padding: '14px 18px' }}>
                {/* Top row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e3a5f', flex: 1 }}>{item.controlId} — {item.controlName}</span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 800, color: rc, background: rb, padding: '2px 8px', borderRadius: 4, border: `1px solid ${rc}`, whiteSpace: 'nowrap' }}>
                    {item.riskLevel}
                  </span>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: auditColor, background: auditBg, padding: '2px 9px', borderRadius: 10, whiteSpace: 'nowrap' }}>
                    {t('AsmDashboard.auditLabel', { status: item.auditStatus })}
                  </span>
                </div>

                {item.description && (
                  <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: '0 0 10px', lineHeight: 1.5 }}>{item.description}</p>
                )}

                {/* CWE hit tags */}
                <div style={{ marginBottom: 6 }}>
                  <span style={{ fontSize: '0.68rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 6 }}>{t('AsmDashboard.asmDetected')}</span>
                  {item.asmCweHits.map(cwe => (
                    <span key={cwe} style={{ ...S.cweTag, marginRight: 4, fontSize: '0.7rem', padding: '2px 8px' }}>{cwe}</span>
                  ))}
                </div>

                {/* Full control CWE scope */}
                <div>
                  <span style={{ fontSize: '0.68rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 6 }}>{t('AsmDashboard.controlCovers')}</span>
                  {item.controlCwes.map(cwe => {
                    const isHit = item.asmCweHits.includes(cwe);
                    return (
                      <span key={cwe} style={{ fontSize: '0.7rem', color: isHit ? '#7c3aed' : '#9ca3af', background: isHit ? '#f5f3ff' : '#f9fafb', border: `1px solid ${isHit ? '#ddd6fe' : '#e5e7eb'}`, padding: '1px 7px', borderRadius: 4, marginRight: 4, fontFamily: 'monospace', fontWeight: isHit ? 700 : 400 }}>{cwe}</span>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

}

// ══════════════════════════════════════════════════════════════════════════════
// FindingsTab — defined at module level so React never recreates the component
// type on parent re-renders (avoids unmount/remount + focus loss on inputs).
// Props: selectedDomainId, setSelectedDomainId, domainNames, reportData,
//        loadingReport, loadReport, scans, scanningIds, isAdmin
// ══════════════════════════════════════════════════════════════════════════════

function DomainsTab({
  domainPaging, stats, completedScans, activeScans, scanQuota,
  dSearchInput, setDSearchInput, dSearch, dPage, setDPage,
  domains, latestScanFor, isScanningDomain, scanStagesFor, nextStepFor,
  handleViewFindings, isAdmin, handleStartScan,
  setAdvDomainId, setPocDomainId, handleLoadAdvTargets, setPocResults,
  setTab, setIntelSubTab, handleDeleteDomain,
}) {
  const { t } = useTranslation();
  const dSearchRef = useRef(null);
  const totalPages = domainPaging.totalPages;

  const lightLeft = scanQuota
    ? (scanQuota.light?.weeklyFreeLeft ?? 0) + (scanQuota.light?.purchased ?? 0)
    : null;
  const fullLeft = scanQuota
    ? (scanQuota.full?.weeklyFreeLeft ?? scanQuota.weeklyFreeLeft ?? 0) + (scanQuota.full?.purchased ?? scanQuota.purchased ?? 0)
    : null;

  const paginBtn = {
    padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer',
    border: '1.5px solid #e5e7eb', background: '#fff', color: '#374151',
    fontWeight: 400, lineHeight: 1.4,
  };

  return (
    <div>
      {/* ── Metrics strip ── */}
      <div style={S.metricsStrip}>
        {[
          { label: t('AsmDashboard.domains'),    value: stats.domainCount,    color: '#4c75af' },
          { label: t('AsmDashboard.subdomains'), value: stats.subdomainCount, color: '#7c3aed' },
          { label: t('AsmDashboard.ips'),        value: stats.ipCount,        color: '#0369a1' },
          { label: t('AsmDashboard.openPorts'),  value: stats.portCount,      color: '#0891b2' },
          { label: t('AsmDashboard.completed'),  value: completedScans,       color: '#16a34a' },
        ].map(m => (
          <div key={m.label} style={S.metricItem}>
            <span style={{ fontSize: 22, fontWeight: 700, color: m.color, lineHeight: 1 }}>{m.value}</span>
            <span style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{m.label}</span>
          </div>
        ))}
        {activeScans > 0 && (
          <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
            <span style={pill('#0369a1', '#e0f2fe')}>● {t('AsmDashboard.scansRunning', { count: activeScans })}</span>
          </div>
        )}
      </div>

      {/* ── Search bar ── */}
      {domainPaging.totalElements > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              color: '#9ca3af', fontSize: 13, pointerEvents: 'none' }}>🔍</span>
            <input
              ref={dSearchRef}
              value={dSearchInput}
              onChange={e => setDSearchInput(e.target.value)}
              placeholder={t('AsmDashboard.searchDomainOrOrg')}
              style={{ width: '100%', padding: '6px 10px 6px 30px', borderRadius: 7,
                border: '1.5px solid #e5e7eb', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }}
            />
            {dSearchInput && (
              <button onClick={() => { setDSearchInput(''); dSearchRef.current?.focus(); }}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14 }}>✕</button>
            )}
          </div>
          <span style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
            {t('AsmDashboard.domainCount', { count: domainPaging.totalElements })}
            {totalPages > 1 && ` · ${t('AsmDashboard.pageOfShort', { page: dPage + 1, total: totalPages })}`}
          </span>
        </div>
      )}

      {/* ── Domain list ── */}
      {domainPaging.totalElements === 0 && !dSearch ? (
        <div style={S.empty}>{t('AsmDashboard.noDomainsYet')}</div>
      ) : domains.length === 0 && dSearch ? (
        <div style={S.empty}>{t('AsmDashboard.noDomainsMatch', { query: dSearch })}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {domains.map(d => {
            const scan     = latestScanFor(d.id);
            const scanning = isScanningDomain(d.id);
            const stages   = scanStagesFor(d.id);
            const hint     = nextStepFor(stages);
            const subCnt   = d.subdomainCount ?? 0;
            const ipCnt    = d.ipCount        ?? 0;
            const portCnt  = d.openPortCount  ?? 0;

            return (
              <div key={d.id} style={{
                background: '#fff', borderRadius: 10, padding: '16px 20px',
                border: `1px solid ${scanning ? '#bfdbfe' : '#e5e7eb'}`,
                borderLeft: `4px solid ${scanning ? '#2563eb' : '#e5e7eb'}`,
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'border-color 0.2s',
              }}>

                {/* Row 1: name + status + org + date */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span
                    onClick={() => handleViewFindings(d)}
                    style={{ fontWeight: 700, color: '#111827', fontSize: '0.95rem', cursor: 'pointer', textDecoration: 'none' }}
                    title={t('AsmDashboard.viewFindings')}
                  >🌐 {d.name}</span>
                  <StatusPill status={scan?.status} scanning={scanning} />
                  {isAdmin && d.organizationName && (
                    <span style={{ fontSize: '0.72rem', color: '#4c75af', background: '#eff6ff', padding: '2px 8px', borderRadius: 8, fontWeight: 600 }}>
                      {d.organizationName}
                      {d.addedBy && d.addedBy !== 'system' && (
                        <span style={{ color: '#64748b', fontWeight: 400 }}> · {d.addedBy}</span>
                      )}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                    {d.lastScanDate ? fmtDate(d.lastScanDate) : t('AsmDashboard.notYetScanned')}
                    {scan?.scanType && (
                      <span style={{ marginLeft: 4, color: '#64748b', fontWeight: 600 }}>({scan.scanType})</span>
                    )}
                  </span>
                </div>

                {/* Row 2: asset counts */}
                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: 10, display: 'flex', gap: 10 }}>
                  <span><b style={{ color: '#374151' }}>{subCnt}</b> {t('AsmDashboard.subdomainsLower')}</span>
                  <span style={{ color: '#d1d5db' }}>·</span>
                  <span><b style={{ color: '#374151' }}>{ipCnt}</b> {t('AsmDashboard.ipsLower')}</span>
                  <span style={{ color: '#d1d5db' }}>·</span>
                  <span><b style={{ color: '#374151' }}>{portCnt}</b> {t('AsmDashboard.openPortsLower')}</span>
                </div>

                {/* Row 3: scan pipeline stages */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
                  {[
                    { id: 'light', icon: '⚡', label: t('AsmDashboard.stageLight'),   color: '#0369a1' },
                    { id: 'full',  icon: '▶',  label: t('AsmDashboard.stageFull'),    color: '#16a34a' },
                    { id: 'deep',  icon: '🔬', label: t('AsmDashboard.stageDeep'),    color: '#7c3aed' },
                    { id: 'rt',    icon: '🎯', label: t('AsmDashboard.stageRedTeam'), color: '#d97706' },
                  ].map((stage, si) => {
                    const done = stages[stage.id] || false;
                    return (
                      <React.Fragment key={stage.id}>
                        {si > 0 && <span style={{ color: '#d1d5db', fontSize: 12, flexShrink: 0 }}>›</span>}
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
                          background: done ? stage.color + '15' : '#f9fafb',
                          color: done ? stage.color : '#9ca3af',
                          border: `1px solid ${done ? stage.color + '35' : '#e5e7eb'}`,
                        }}>
                          {stage.icon} {stage.label} {done ? '✓' : '○'}
                        </span>
                      </React.Fragment>
                    );
                  })}
                </div>

                {/* Next step hint */}
                {hint && (
                  <div style={{ fontSize: '0.75rem', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ color: hint.color, fontWeight: 700 }}>→</span>
                    <span style={{ color: '#475569' }}>{hint.text}</span>
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button onClick={() => handleViewFindings(d)} style={S.domBtn('primary')}>
                    {t('AsmDashboard.findingsBtn')}
                  </button>

                  {scanning ? (
                    <span style={{ ...pill('#0369a1', '#e0f2fe'), padding: '6px 14px' }}>● {t('AsmDashboard.statusScanning')}</span>
                  ) : (
                    <>
                      <button
                        onClick={() => handleStartScan(d.id, 'LIGHT', 'FULL')}
                        title={t('AsmDashboard.lightScanTitle')}
                        style={S.domBtn('light')}
                      >
                        {t('AsmDashboard.lightBtn')}
                        {lightLeft !== null && (
                          <span style={{ fontSize: 10, opacity: 0.85, marginLeft: 3 }}>({lightLeft})</span>
                        )}
                      </button>
                      <button
                        onClick={() => handleStartScan(d.id, 'FULL', 'INCREMENTAL')}
                        title={t('AsmDashboard.fullScanTitle')}
                        style={S.domBtn('full')}
                      >
                        {t('AsmDashboard.fullBtn')}
                        {fullLeft !== null && (
                          <span style={{ fontSize: 10, opacity: 0.85, marginLeft: 3 }}>({fullLeft})</span>
                        )}
                      </button>
                      <button
                        onClick={() => handleStartScan(d.id, 'FULL', 'FULL')}
                        title={t('AsmDashboard.rescanTitle')}
                        style={S.domBtn('rescan')}
                      >{t('AsmDashboard.rescanBtn')}</button>
                      <button
                        onClick={() => {
                          const id = String(d.id);
                          setAdvDomainId(id); setPocDomainId(id);
                          handleLoadAdvTargets(id); setPocResults([]);
                          setTab('intelligence'); setIntelSubTab('deep');
                        }}
                        title={t('AsmDashboard.deepScanTitle')}
                        style={S.domBtn('deep')}
                      >{t('AsmDashboard.deepScanBtn')}</button>
                    </>
                  )}

                  <button
                    onClick={() => handleDeleteDomain(d.id, d.name)}
                    style={{ ...S.domBtn('delete'), marginLeft: 'auto' }}
                    title={t('AsmDashboard.deleteDomainTitle')}
                  >🗑</button>
                </div>

              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 18 }}>
          <button onClick={() => setDPage(0)} disabled={dPage === 0}
            style={{ ...paginBtn, opacity: dPage === 0 ? 0.35 : 1 }}>«</button>
          <button onClick={() => setDPage(p => p - 1)} disabled={dPage === 0}
            style={{ ...paginBtn, opacity: dPage === 0 ? 0.35 : 1 }}>‹</button>

          {Array.from({ length: totalPages }, (_, i) => i)
            .filter(i => Math.abs(i - dPage) <= 2)
            .map(i => (
              <button key={i} onClick={() => setDPage(i)}
                style={{ ...paginBtn,
                  background: i === dPage ? '#6366f1' : '#fff',
                  color:      i === dPage ? '#fff'    : '#374151',
                  border:     i === dPage ? '1.5px solid #6366f1' : '1.5px solid #e5e7eb',
                  fontWeight: i === dPage ? 700 : 400 }}>
                {i + 1}
              </button>
            ))}

          <button onClick={() => setDPage(p => p + 1)} disabled={dPage >= totalPages - 1}
            style={{ ...paginBtn, opacity: dPage >= totalPages - 1 ? 0.35 : 1 }}>›</button>
          <button onClick={() => setDPage(totalPages - 1)} disabled={dPage >= totalPages - 1}
            style={{ ...paginBtn, opacity: dPage >= totalPages - 1 ? 0.35 : 1 }}>»</button>

          <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: 4 }}>
            {t('AsmDashboard.pageOfTotal', { page: dPage + 1, total: totalPages, count: domainPaging.totalElements })}
          </span>
        </div>
      )}
    </div>
  );
}

function FindingsTab({
  selectedDomainId, setSelectedDomainId,
  domainNames, reportData, loadingReport, loadReport,
  scans, scanningIds, isAdmin,
}) {
  const { t } = useTranslation();
  const [sevFilter,    setSevFilter]    = useState('ALL');
  const [moduleFilter, setModuleFilter] = useState('ALL');
  const [phaseFilter,  setPhaseFilter]  = useState('ALL');
  const [deepStatus,   setDeepStatus]   = useState('ALL');
  const [findSearch,   setFindSearch]   = useState('');
  const [expandedSubs, setExpandedSubs] = useState({});

  // ── Sibling domain state ───────────────────────────────────────────────────
  const [siblings,       setSiblings]       = useState([]);
  const [siblingsLoaded, setSiblingsLoaded] = useState(false);
  const [siblingBusy,    setSiblingBusy]    = useState({}); // siblingId → true while action in-flight

  useEffect(() => {
    if (!selectedDomainId) { setSiblings([]); setSiblingsLoaded(false); return; }
    setSiblingsLoaded(false);
    axiosInstance.get(`/asm/domains/${selectedDomainId}/sibling-domains`)
      .then(r => { setSiblings(r.data || []); setSiblingsLoaded(true); })
      .catch(() => { setSiblings([]); setSiblingsLoaded(true); });
  }, [selectedDomainId, reportData]);

  // Auto-expand subdomains with open ports when report loads
  useEffect(() => {
    if (!reportData) { setExpandedSubs({}); return; }
    const autoExpand = {};
    (reportData.subdomains || []).forEach((sub, sIdx) => {
      const hasOpenPorts = (sub.ipAddresses || []).some(ip =>
        (ip.ports || []).some(p => p.state === 'open')
      );
      if (hasOpenPorts) autoExpand[sIdx] = true;
    });
    setExpandedSubs(autoExpand);
  }, [reportData]);

  // Local helpers (use scans prop)
  const latestScanFor = (domainId) => scans.find(s => s.domainId === domainId);
  const scanStagesFor = (domainId) => {
    const ds = scans.filter(s => s.domainId === domainId);
    return {
      light: ds.some(s => (s.scanType || 'FULL').toUpperCase() === 'LIGHT' && s.status === 'COMPLETED'),
      full:  ds.some(s => (s.scanType || 'FULL').toUpperCase() === 'FULL'  && s.status === 'COMPLETED'),
      deep:  ds.some(s => (s.scanType || 'FULL').toUpperCase() === 'DEEP'  && s.status === 'COMPLETED'),
    };
  };

  // Computed from props
  const cveStats          = countCves(reportData);
  const totalCves         = Object.values(cveStats).reduce((a, b) => a + b, 0);
  const selectedDomainName = (domainNames.find(d => String(d.id) === selectedDomainId) || {}).name || t('AsmDashboard.domainFallback');

  const cvePassesFilters = (cve) => {
    if (sevFilter !== 'ALL' && (cve.severity || '').toUpperCase() !== sevFilter) return false;
    if (moduleFilter !== 'ALL' && cveModuleCategory(cve.cveId) !== moduleFilter) return false;
    if (phaseFilter !== 'ALL' && getCvePhase(cve.cveId) !== phaseFilter) return false;
    if (phaseFilter === 'DEEP' && deepStatus !== 'ALL') {
      const fp = cve.penTestResult?.result === 'NOT_VULNERABLE';
      if (deepStatus === 'CONFIRMED' && fp)  return false;
      if (deepStatus === 'FP'        && !fp) return false;
    }
    if (findSearch) {
      const q = findSearch.toLowerCase();
      if (!cve.cveId?.toLowerCase().includes(q) && !(cve.description || '').toLowerCase().includes(q)) return false;
    }
    return true;
  };

  const anyFilterActive = sevFilter !== 'ALL' || moduleFilter !== 'ALL' || phaseFilter !== 'ALL' || !!findSearch;

  const filteredSubs = (reportData?.subdomains || []).filter(sub => {
    const hasCveMatch = (sub.ipAddresses || []).some(ip =>
      (ip.ports || []).some(port =>
        (port.applications || []).some(app =>
          (app.cveMatches || []).some(cvePassesFilters)
        )
      )
    );
    if (anyFilterActive) return hasCveMatch;
    if (!findSearch) return true;
    const q = findSearch.toLowerCase();
    if (sub.name?.toLowerCase().includes(q)) return true;
    for (const ip of (sub.ipAddresses || [])) {
      if (ip.address?.includes(q)) return true;
    }
    return hasCveMatch;
  });

  // ── Compute funnel counts from raw reportData (not filtered) ─────────────
  const domainStages = selectedDomainId ? scanStagesFor(Number(selectedDomainId)) : null;
  const funnel = { light: 0, full: 0, deepConfirmed: 0, deepFP: 0, deepUnverified: 0 };
  if (reportData) {
    for (const sub of reportData.subdomains || [])
      for (const ip of sub.ipAddresses || [])
        for (const port of ip.ports || [])
          for (const app of port.applications || [])
            for (const cve of app.cveMatches || []) {
              const phase = getCvePhase(cve.cveId);
              if (phase === 'LIGHT') { funnel.light++; }
              else if (phase === 'FULL') { funnel.full++; }
              else {
                const r = cve.penTestResult?.result;
                if (r === 'NOT_VULNERABLE') funnel.deepFP++;
                else if (domainStages?.deep)  funnel.deepConfirmed++;
                else                          funnel.deepUnverified++;
              }
            }
  }
  const totalDeep = funnel.deepConfirmed + funnel.deepFP + funnel.deepUnverified;

  // ── Per-module counts (respecting all filters except module filter itself) ─
  const modCounts = {};
  MODULE_CATEGORIES.filter(m => m.id !== 'ALL').forEach(m => { modCounts[m.id] = 0; });
  if (reportData) {
    for (const sub of reportData.subdomains || [])
      for (const ip of sub.ipAddresses || [])
        for (const port of ip.ports || [])
          for (const app of port.applications || [])
            for (const cve of app.cveMatches || []) {
              if (sevFilter !== 'ALL' && (cve.severity || '').toUpperCase() !== sevFilter) continue;
              if (phaseFilter !== 'ALL' && getCvePhase(cve.cveId) !== phaseFilter) continue;
              const cat = cveModuleCategory(cve.cveId);
              if (cat in modCounts) modCounts[cat]++;
            }
  }
  const activeModules = MODULE_CATEGORIES.filter(m => m.id !== 'ALL' &&
    (phaseFilter === 'ALL' ? modCounts[m.id] > 0 : m.phase === phaseFilter && modCounts[m.id] >= 0));

  const allExpanded = filteredSubs.length > 0 && filteredSubs.every((_, i) => expandedSubs[i]);

  const PHASE_BTNS = [
    { id: 'ALL',   label: t('AsmDashboard.allPhases'),    color: '#4c75af' },
    { id: 'LIGHT', label: t('AsmDashboard.lightRecon'),   color: '#0891b2' },
    { id: 'FULL',  label: t('AsmDashboard.fullScanPhase'),color: '#16a34a' },
    { id: 'DEEP',  label: t('AsmDashboard.deepScanPhase'),color: '#7c3aed' },
  ];

  return (
    <div>

      {/* ── Row 0: Domain autocomplete + Search + Export ── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
        <DomainAutocomplete
          value={selectedDomainId}
          onChange={id => { setSelectedDomainId(id); if (id) loadReport(id); }}
          domainNames={domainNames}
          placeholder={t('AsmDashboard.selectDomainPlaceholder')}
          style={{ flex: '0 0 auto', minWidth: 220 }}
        />
        <input
          type="text"
          placeholder={t('AsmDashboard.searchCvePlaceholder')}
          value={findSearch}
          onChange={e => setFindSearch(e.target.value)}
          style={{ ...S.searchInput, flex: 1, minWidth: 160, opacity: reportData ? 1 : 0.5 }}
          disabled={!reportData}
        />
        {reportData && (
          <button
            onClick={() => exportPdf(reportData, selectedDomainName, fmtDate(latestScanFor(Number(selectedDomainId))?.endTime))}
            style={{ padding: '8px 16px', borderRadius: 7, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' }}
          >{t('AsmDashboard.exportPdf')}</button>
        )}
      </div>

      {/* ── Finding Funnel ── */}
      {reportData && !loadingReport && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr auto 1fr', alignItems: 'stretch', gap: 0, marginBottom: 14, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <button onClick={() => { setPhaseFilter(phaseFilter === 'LIGHT' ? 'ALL' : 'LIGHT'); setModuleFilter('ALL'); setDeepStatus('ALL'); }}
            style={{ padding: '14px 18px', background: phaseFilter === 'LIGHT' ? '#e0f2fe' : '#fff', border: 'none', cursor: 'pointer', textAlign: 'left', borderRight: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#0891b2', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>{t('AsmDashboard.funnelLightRecon')}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: domainStages?.light ? '#0369a1' : '#94a3b8', lineHeight: 1 }}>{funnel.light}</div>
            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 3 }}>{t('AsmDashboard.findingsLower')}</div>
            <div style={{ marginTop: 5, fontSize: '0.68rem', fontWeight: 600, color: domainStages?.light ? '#0891b2' : '#9ca3af' }}>
              {domainStages?.light ? t('AsmDashboard.completedCheck') : t('AsmDashboard.notRun')}
            </div>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 10px', color: '#d1d5db', fontSize: '1.2rem', background: '#fafafa', borderRight: '1px solid #e5e7eb' }}>›</div>
          <button onClick={() => { setPhaseFilter(phaseFilter === 'FULL' ? 'ALL' : 'FULL'); setModuleFilter('ALL'); setDeepStatus('ALL'); }}
            style={{ padding: '14px 18px', background: phaseFilter === 'FULL' ? '#f0fdf4' : '#fff', border: 'none', cursor: 'pointer', textAlign: 'left', borderRight: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>{t('AsmDashboard.funnelFullScan')}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: domainStages?.full ? '#15803d' : '#94a3b8', lineHeight: 1 }}>{funnel.full}</div>
            <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 3 }}>{t('AsmDashboard.findingsLower')}</div>
            <div style={{ marginTop: 5, fontSize: '0.68rem', fontWeight: 600, color: domainStages?.full ? '#16a34a' : '#9ca3af' }}>
              {domainStages?.full ? t('AsmDashboard.completedCheck') : t('AsmDashboard.notRun')}
            </div>
          </button>
          <div style={{ display: 'flex', alignItems: 'center', padding: '0 10px', color: '#d1d5db', fontSize: '1.2rem', background: '#fafafa', borderRight: '1px solid #e5e7eb' }}>›</div>
          <button onClick={() => { setPhaseFilter(phaseFilter === 'DEEP' ? 'ALL' : 'DEEP'); setModuleFilter('ALL'); setDeepStatus('ALL'); }}
            style={{ padding: '14px 18px', background: phaseFilter === 'DEEP' ? '#faf5ff' : '#fff', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>{t('AsmDashboard.funnelDeepScan')}</div>
            {domainStages?.deep ? (
              <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#16a34a', lineHeight: 1 }}>{funnel.deepConfirmed}</div>
                  <div style={{ fontSize: '0.65rem', color: '#16a34a', fontWeight: 600, marginTop: 2 }}>{t('AsmDashboard.confirmedLower')}</div>
                </div>
                {funnel.deepFP > 0 && (
                  <div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#9ca3af', lineHeight: 1 }}>{funnel.deepFP}</div>
                    <div style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600, marginTop: 2 }}>{t('AsmDashboard.falsePositiveCount', { count: funnel.deepFP })}</div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#94a3b8', lineHeight: 1 }}>{totalDeep || '—'}</div>
                <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 3 }}>{t('AsmDashboard.unverified')}</div>
              </>
            )}
            <div style={{ marginTop: 5, fontSize: '0.68rem', fontWeight: 600, color: domainStages?.deep ? '#7c3aed' : '#9ca3af' }}>
              {domainStages?.deep ? t('AsmDashboard.completedCheck') : t('AsmDashboard.runDeepToValidate')}
            </div>
          </button>
        </div>
      )}

      {/* ── Filter panel ── */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={S.filterLabel}>{t('AsmDashboard.phase')}</span>
          {PHASE_BTNS.map(p => (
            <button key={p.id} onClick={() => { setPhaseFilter(p.id); setModuleFilter('ALL'); setDeepStatus('ALL'); }} style={{
              ...S.sevBtn,
              background: phaseFilter === p.id ? p.color : '#fff',
              color: phaseFilter === p.id ? '#fff' : '#374151',
              border: `1px solid ${phaseFilter === p.id ? p.color : '#e2e8f0'}`,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}>{p.label}</button>
          ))}
          <div style={{ width: 1, height: 20, background: '#e2e8f0', margin: '0 4px' }} />
          <span style={S.filterLabel}>{t('AsmDashboard.severity')}</span>
          {['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(s => (
            <button key={s} onClick={() => setSevFilter(s)} style={{
              ...S.sevBtn,
              background: sevFilter === s ? (s === 'ALL' ? '#374151' : getSev(s).color) : '#fff',
              color: sevFilter === s ? '#fff' : '#374151',
              border: `1px solid ${sevFilter === s ? (s === 'ALL' ? '#374151' : getSev(s).color) : '#e2e8f0'}`,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}>{s === 'ALL' ? t('AsmDashboard.all') : s}</button>
          ))}
        </div>
        {phaseFilter === 'DEEP' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, paddingLeft: 10, borderLeft: '3px solid #7c3aed' }}>
            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 }}>{t('AsmDashboard.deepResult')}</span>
            {[
              { id: 'ALL',       label: t('AsmDashboard.all'),                color: '#7c3aed' },
              { id: 'CONFIRMED', label: t('AsmDashboard.confirmedFilter'),    color: '#16a34a' },
              { id: 'FP',        label: t('AsmDashboard.falsePositivesFilter'), color: '#6b7280' },
            ].map(d => (
              <button key={d.id} onClick={() => setDeepStatus(d.id)} style={{
                padding: '4px 12px', borderRadius: 20, border: `1.5px solid ${deepStatus === d.id ? d.color : '#e2e8f0'}`,
                background: deepStatus === d.id ? d.color : '#fff',
                color: deepStatus === d.id ? '#fff' : '#374151',
                fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
              }}>{d.label}</button>
            ))}
          </div>
        )}
        {activeModules.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={S.filterLabel}>{t('AsmDashboard.module')}</span>
            {activeModules.map(m => {
              const cnt = modCounts[m.id];
              const active = moduleFilter === m.id;
              return (
                <button key={m.id} onClick={() => setModuleFilter(active ? 'ALL' : m.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '4px 10px', borderRadius: 7,
                  border: `1.5px solid ${active ? m.color : m.color + '50'}`,
                  background: active ? m.color : m.color + '12',
                  color: active ? '#fff' : m.color,
                  fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                  opacity: cnt === 0 ? 0.4 : 1,
                }}>
                  {t('AsmDashboard.moduleCat_' + m.id)}
                  {cnt > 0 && <span style={{ background: active ? 'rgba(255,255,255,0.28)' : m.color + '30', borderRadius: 20, padding: '0 5px', fontSize: '0.65rem', fontWeight: 700 }}>{cnt}</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Asset + CVE summary strip ── */}
      {reportData && !loadingReport && (() => {
        const totalIps   = (reportData.subdomains || []).reduce((n, s) => n + (s.ipAddresses || []).length, 0);
        const totalPorts = (reportData.subdomains || []).reduce((n, s) =>
          n + (s.ipAddresses || []).reduce((m, ip) =>
            m + (ip.ports || []).filter(p => p.state === 'open').length, 0), 0);
        return (
          <div style={{ ...S.cveSummaryRow, marginBottom: 14 }}>
            <div style={{ ...S.cveSumCard, borderLeft: '4px solid #7c3aed' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#7c3aed', lineHeight: 1 }}>{(reportData.subdomains || []).length}</span>
              <span style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('AsmDashboard.subdomains')}</span>
            </div>
            <div style={{ ...S.cveSumCard, borderLeft: '4px solid #0369a1' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0369a1', lineHeight: 1 }}>{totalIps}</span>
              <span style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('AsmDashboard.ips')}</span>
            </div>
            <div style={{ ...S.cveSumCard, borderLeft: '4px solid #0891b2' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#0891b2', lineHeight: 1 }}>{totalPorts}</span>
              <span style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('AsmDashboard.openPorts')}</span>
            </div>
            <div style={{ borderLeft: '1px solid #e5e7eb', margin: '0 2px' }} />
            {Object.entries(cveStats).map(([sev, count]) => count > 0 ? (
              <div key={sev} style={{ ...S.cveSumCard, borderLeft: `4px solid ${getSev(sev).color}` }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 800, color: getSev(sev).color, lineHeight: 1 }}>{count}</span>
                <span style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{sev}</span>
              </div>
            ) : null)}
            <div style={{ ...S.cveSumCard, borderLeft: '4px solid #4c75af' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 800, color: '#4c75af', lineHeight: 1 }}>{totalCves}</span>
              <span style={{ fontSize: '0.65rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('AsmDashboard.total')}</span>
            </div>
            <button
              onClick={() => allExpanded ? setExpandedSubs({}) : (() => { const all = {}; filteredSubs.forEach((_, i) => { all[i] = true; }); setExpandedSubs(all); })()}
              style={{ marginLeft: 'auto', alignSelf: 'center', padding: '6px 14px', borderRadius: 7, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: '0.78rem', color: '#374151', whiteSpace: 'nowrap' }}
            >{allExpanded ? t('AsmDashboard.collapseAll') : t('AsmDashboard.expandAll')}</button>
          </div>
        );
      })()}

      {/* ── Empty states ── */}
      {!selectedDomainId && <div style={S.empty}>{t('AsmDashboard.selectDomainToExplore')}</div>}
      {loadingReport && <div style={S.empty}><div style={{ fontSize: '2.5rem', marginBottom: 12 }}>⟳</div>{t('AsmDashboard.loadingAttackSurface')}</div>}
      {reportData && !loadingReport && filteredSubs.length === 0 && <div style={S.empty}>{t('AsmDashboard.noFindingsMatchFilters')}</div>}

      {/* ── Findings tree ── */}
      {/* ── Related / Sibling Domains panel ──────────────────────────────── */}
      {siblingsLoaded && siblings.length > 0 && (
        <div style={{ marginBottom: 24, background: '#fff', borderRadius: 10,
          border: '1px solid #e0e7ff', boxShadow: '0 1px 4px rgba(99,102,241,0.07)' }}>
          <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid #e0e7ff',
            display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.1rem' }}>🔗</span>
            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#3730a3' }}>
              {t('AsmDashboard.relatedDomainsDiscovered', { count: siblings.length })}
            </span>
            <span style={{ fontSize: '0.75rem', color: '#6366f1', background: '#eef2ff',
              padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
              {t('AsmDashboard.autoDiscoveredDuringScan')}
            </span>
          </div>
          <div style={{ padding: '6px 0 4px' }}>
            {siblings.map(s => {
              const methodColor = {
                API_DOC:      { bg: '#fefce8', color: '#92400e', label: t('AsmDashboard.methodApiDoc') },
                JS_REFERENCE: { bg: '#f0fdf4', color: '#166534', label: t('AsmDashboard.methodJsFile') },
                HTML_LINK:    { bg: '#eff6ff', color: '#1e40af', label: t('AsmDashboard.methodHtmlLink') },
                TLS_SAN:      { bg: '#fdf4ff', color: '#6b21a8', label: t('AsmDashboard.methodTlsSan') },
              }[s.discoveryMethod] || { bg: '#f3f4f6', color: '#374151', label: s.discoveryMethod };

              return (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 18px', borderBottom: '1px solid #f1f5f9',
                  flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem',
                    fontFamily: 'monospace', flex: '1 1 180px' }}>
                    🌐 {s.name}
                  </span>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px',
                    borderRadius: 10, background: methodColor.bg, color: methodColor.color }}>
                    {methodColor.label}
                  </span>
                  {s.sourceUrl && (
                    <span style={{ fontSize: '0.72rem', color: '#64748b', maxWidth: 300,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      flex: '1 1 200px' }} title={s.sourceUrl}>
                      {t('AsmDashboard.viaSource', { url: s.sourceUrl })}
                    </span>
                  )}
                  <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                    <button
                      disabled={!!siblingBusy[s.id]}
                      title={t('AsmDashboard.addSiblingTitle')}
                      onClick={async () => {
                        setSiblingBusy(b => ({ ...b, [s.id]: true }));
                        try {
                          const triggeredBy = localStorage.getItem('userName') || 'user';
                          const r = await axiosInstance.post(
                            `/asm/domains/${selectedDomainId}/sibling-domains/${s.id}/add-to-asm`,
                            { triggerScan: true, triggeredBy });
                          const msg = r.data?.alreadyExists
                            ? t('AsmDashboard.siblingAlreadyTracked', { name: s.name })
                            : t('AsmDashboard.siblingAddedScanStarted', { name: s.name });
                          Swal.fire({ icon: 'success', title: t('AsmDashboard.done'), text: msg,
                            timer: 3000, showConfirmButton: false });
                          setSiblings(prev => prev.filter(x => x.id !== s.id));
                        } catch (err) {
                          Swal.fire({ icon: 'error', title: t('AsmDashboard.error'),
                            text: err?.response?.data || t('AsmDashboard.failedAddDomain') });
                        } finally {
                          setSiblingBusy(b => ({ ...b, [s.id]: false }));
                        }
                      }}
                      style={{ padding: '4px 12px', borderRadius: 6, fontSize: '0.78rem',
                        fontWeight: 600, cursor: siblingBusy[s.id] ? 'not-allowed' : 'pointer',
                        background: siblingBusy[s.id] ? '#e5e7eb' : '#4f46e5',
                        color: siblingBusy[s.id] ? '#9ca3af' : '#fff', border: 'none' }}>
                      {siblingBusy[s.id] ? '…' : t('AsmDashboard.addAndScan')}
                    </button>
                    <button
                      disabled={!!siblingBusy[s.id]}
                      title={t('AsmDashboard.dismissSiblingTitle')}
                      onClick={async () => {
                        setSiblingBusy(b => ({ ...b, [s.id]: true }));
                        try {
                          await axiosInstance.delete(
                            `/asm/domains/${selectedDomainId}/sibling-domains/${s.id}`);
                          setSiblings(prev => prev.filter(x => x.id !== s.id));
                        } catch { /* ignore */ }
                        finally { setSiblingBusy(b => ({ ...b, [s.id]: false })); }
                      }}
                      style={{ padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem',
                        fontWeight: 600, cursor: siblingBusy[s.id] ? 'not-allowed' : 'pointer',
                        background: '#f3f4f6', color: '#6b7280',
                        border: '1px solid #e5e7eb' }}>
                      ✕
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {reportData && !loadingReport && filteredSubs.map((sub, sIdx) => {
        const isOpen = expandedSubs[sIdx];
        let subCveCount = 0, subPortCount = 0;
        for (const ip of (sub.ipAddresses || []))
          for (const port of (ip.ports || []))
            if (port.state === 'open') {
              subPortCount++;
              for (const app of (port.applications || []))
                for (const cve of (app.cveMatches || []))
                  if (cvePassesFilters(cve)) subCveCount++;
            }

        return (
          <div key={sIdx} style={S.subdomainBlock}>
            <div style={S.subdomainHeader} onClick={() => setExpandedSubs(prev => ({ ...prev, [sIdx]: !prev[sIdx] }))}>
              <span style={S.chevron}>{isOpen ? '▾' : '▸'}</span>
              <span style={{ fontWeight: 600, color: '#1e3a5f' }}>🌐 {sub.name}</span>
              <div style={S.subdomainMeta}>
                <span style={S.metaChip}>{t('AsmDashboard.ipCount', { count: (sub.ipAddresses || []).length })}</span>
                {subPortCount > 0 && <span style={{ ...S.metaChip, background: '#eff6ff', color: '#0369a1', border: '1px solid #bfdbfe' }}>{t('AsmDashboard.portCount', { count: subPortCount })}</span>}
                {subCveCount > 0 && <span style={{ ...S.metaChip, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }}>{t('AsmDashboard.findingCount', { count: subCveCount })}</span>}
              </div>
            </div>

            {isOpen && (sub.ipAddresses || []).map((ip, iIdx) => {
              const openPorts = (ip.ports || []).filter(p => p.state === 'open');
              return (
                <div key={iIdx} style={S.ipBlock}>
                  <div style={S.ipRow}>
                    <span style={S.ipIcon}>⬡</span>
                    <strong style={{ color: '#1e3a5f' }}>{ip.address}</strong>
                    <span style={S.metaChip}>{t('AsmDashboard.openPortCount', { count: openPorts.length })}</span>
                  </div>
                  {openPorts.length === 0 && <p style={{ margin: '4px 0 0 24px', fontSize: '0.8rem', color: '#9ca3af' }}>{t('AsmDashboard.noOpenPortsFound')}</p>}

                  <div style={S.portGrid}>
                    {openPorts.map((port, pIdx) => {
                      const apps  = port.applications || [];
                      const cves  = apps.flatMap(a => (a.cveMatches || []).filter(cvePassesFilters));
                      const svcLbl = WELL_KNOWN_PORTS[port.portNumber];
                      return (
                        <div key={pIdx} style={S.portCard}>
                          <div style={S.portCardHeader}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                              <span style={S.portNum}>{port.portNumber}<span style={S.portProto}>/{(port.protocol || 'tcp').toLowerCase()}</span></span>
                              {svcLbl && <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', background: '#f3f4f6', padding: '1px 6px', borderRadius: 4 }}>{svcLbl}</span>}
                            </div>
                            <span style={S.openBadge}>open</span>
                          </div>

                          {apps.map((app, aIdx) => (
                            <div key={aIdx} style={S.appRow}>
                              <span style={S.appName}>{app.name || t('AsmDashboard.unknown')}</span>
                              {app.version && app.version !== 'Unknown' && <span style={S.appVersion}>{app.version}</span>}
                            </div>
                          ))}
                          {apps.length === 0 && <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>{t('AsmDashboard.serviceNotFingerprinted')}</p>}
                          {apps.length > 0 && cves.length === 0 && <p style={{ margin: '6px 0 0', fontSize: '0.75rem', color: '#16a34a' }}>{t('AsmDashboard.noVulnsDetected')}</p>}

                          {cves.map((cve, cIdx) => {
                            const sv        = getSev(cve.severity);
                            const penResult = cve.penTestResult?.result;
                            const isFP      = penResult === 'NOT_VULNERABLE';
                            const isExploit = penResult === 'EXPLOITED';
                            const phase     = getCvePhase(cve.cveId);
                            const mc        = moduleCategoryLabel(cve.cveId);
                            const cardStyle = isFP
                              ? { ...S.cveCard, background: '#f9fafb', border: '1.5px dashed #d1d5db', opacity: 0.75 }
                              : { ...S.cveCard, background: sv.bg, borderLeft: `3px solid ${sv.color}` };

                            return (
                              <div key={cIdx} style={cardStyle}>
                                <div style={S.cveTopRow}>
                                  <span style={{ fontWeight: 700, color: isFP ? '#9ca3af' : sv.color, fontSize: '0.8rem',
                                    textDecoration: isFP ? 'line-through' : 'none' }}>
                                    {cve.cveId}
                                  </span>
                                  <SevBadge severity={cve.severity} />
                                  {phase === 'DEEP' && (
                                    <span style={{ fontSize: '0.62rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe' }}>
                                      🔬 DEEP
                                    </span>
                                  )}
                                  {phase === 'LIGHT' && (
                                    <span style={{ fontSize: '0.62rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }}>
                                      ⚡ LIGHT
                                    </span>
                                  )}
                                  <span style={{ fontSize: '0.62rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: mc.color + '15', color: mc.color, border: `1px solid ${mc.color}35` }}>
                                    {t('AsmDashboard.moduleCat_' + mc.id)}
                                  </span>
                                  {cve.exploitabilityScore != null && (
                                    <span style={S.scoreChip}>Score {cve.exploitabilityScore}</span>
                                  )}
                                </div>
                                {isFP && (
                                  <div style={{ margin: '5px 0 4px', padding: '5px 10px', borderRadius: 6, background: '#f3f4f6', border: '1px solid #e5e7eb', fontSize: '0.73rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: '0.9rem' }}>🚫</span>
                                    <span><strong style={{ color: '#374151' }}>{t('AsmDashboard.falsePositiveLabel')}</strong>{t('AsmDashboard.falsePositiveBody')}</span>
                                  </div>
                                )}
                                {isExploit && (
                                  <div style={{ margin: '5px 0 4px', padding: '5px 10px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fca5a5', fontSize: '0.73rem', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: '0.9rem' }}>🚨</span>
                                    <span><strong>{t('AsmDashboard.confirmedExploitableLabel')}</strong>{t('AsmDashboard.confirmedExploitableBody')}</span>
                                  </div>
                                )}
                                {!isFP && !isExploit && phase === 'DEEP' && domainStages?.deep && (
                                  <div style={{ margin: '5px 0 4px', padding: '4px 10px', borderRadius: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: '0.72rem', color: '#15803d', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span>✅</span>
                                    <span><strong>{t('AsmDashboard.deepConfirmedLabel')}</strong>{t('AsmDashboard.deepConfirmedBody')}</span>
                                  </div>
                                )}
                                {!isFP && !isExploit && phase === 'DEEP' && !domainStages?.deep && (
                                  <div style={{ margin: '5px 0 4px', padding: '4px 10px', borderRadius: 6, background: '#fafafa', border: '1px solid #e5e7eb', fontSize: '0.72rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span>🔬</span>
                                    <span>{t('AsmDashboard.pendingDeepValidation')}</span>
                                  </div>
                                )}
                                {!isFP && cve.description && <p style={S.cveDesc}>{cve.description}</p>}
                                {cve.cweIds && cve.cweIds.length > 0 && !isFP && (
                                  <div style={S.cweTagRow}>
                                    {cve.cweIds.map(id => <span key={id} style={S.cweTag}>{id.trim()}</span>)}
                                  </div>
                                )}
                                {cve.penTestResult && !isFP && !isExploit && (
                                  <div style={{ ...S.penTestRow, background: '#f0fdf4', color: '#16a34a', borderColor: '#86efac' }}>
                                    🛡️ <strong>{cve.penTestResult.result}</strong>
                                    {cve.penTestResult.details && ` — ${cve.penTestResult.details.slice(0, 120)}`}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// HistoryTab — module-level to prevent remounts on parent re-renders.
// All local state (search, filters, pagination) persists across parent re-renders.
// ══════════════════════════════════════════════════════════════════════════════
function HistoryTab({ setHistoryTotal }) {
  const { t } = useTranslation();
  const PAGE_SIZE = 15;
  const [hPage,    setHPage]    = useState(0);
  const [hSearch,  setHSearch]  = useState('');
  const [hType,    setHType]    = useState('');
  const [hStatus,  setHStatus]  = useState('');
  const [hData,    setHData]    = useState({ content: [], totalElements: 0, totalPages: 0, number: 0 });
  const [hLoading, setHLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const searchRef  = useRef(null);
  const hDataRef   = useRef({ content: [] });

  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setHSearch(searchInput); setHPage(0); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    setHLoading(true);
    const params = new URLSearchParams({ page: hPage, size: PAGE_SIZE });
    if (hSearch)  params.set('search',   hSearch);
    if (hType)    params.set('scanType', hType);
    if (hStatus)  params.set('status',   hStatus);
    axiosInstance.get(`/asm/scans/page?${params}`)
      .then(r => {
        if (!cancelled) {
          const data = r.data || {};
          setHData(data);
          hDataRef.current = data;
          if (setHistoryTotal && data.totalElements != null) setHistoryTotal(data.totalElements);
        }
      })
      .catch(err => console.error('[HistoryTab] /asm/scans/page failed:', err?.response?.status, err?.message))
      .finally(() => { if (!cancelled) setHLoading(false); });
    return () => { cancelled = true; };
  }, [hPage, hSearch, hType, hStatus, setHistoryTotal, refreshTick]);

  // Auto-refresh every 8s unconditionally while History tab is visible.
  // This ensures newly-started async scans appear without a manual refresh.
  useEffect(() => {
    const id = setInterval(() => setRefreshTick(t => t + 1), 8000);
    return () => clearInterval(id);
  }, []);

  const typeColor = { LIGHT: '#0369a1', FULL: '#16a34a', DEEP: '#7c3aed' };
  const typeIcon  = { LIGHT: '⚡', FULL: '▶', DEEP: '🔬' };

  const paginBtn = {
    padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem', cursor: 'pointer',
    border: '1.5px solid #e5e7eb', background: '#fff', color: '#374151',
    fontWeight: 400, lineHeight: 1.4,
  };

  const filterBtn = (active, label, onClick) => (
    <button onClick={onClick} style={{
      padding: '4px 11px', borderRadius: 6, fontSize: '0.73rem', fontWeight: 600, cursor: 'pointer',
      border: active ? '1.5px solid #6366f1' : '1.5px solid #e5e7eb',
      background: active ? '#eef2ff' : '#fff', color: active ? '#4338ca' : '#6b7280',
      transition: 'all 0.15s',
    }}>{label}</button>
  );

  const { content = [], totalElements = 0, totalPages = 0, number = 0 } = hData;

  return (
    <div style={S.card}>
      {/* ── Controls bar ── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ position: 'relative', flex: '1 1 200px', minWidth: 160 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: '#9ca3af', fontSize: 13, pointerEvents: 'none' }}>🔍</span>
          <input
            ref={searchRef}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder={t('AsmDashboard.searchDomainPlaceholder')}
            style={{ width: '100%', padding: '6px 10px 6px 30px', borderRadius: 7, border: '1.5px solid #e5e7eb',
              fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }}
          />
          {searchInput && (
            <button onClick={() => { setSearchInput(''); searchRef.current?.focus(); }}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14 }}>✕</button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('AsmDashboard.type')}</span>
          {filterBtn(hType === '',      t('AsmDashboard.all'),         () => { setHType('');      setHPage(0); })}
          {filterBtn(hType === 'LIGHT', t('AsmDashboard.filterLight'), () => { setHType('LIGHT'); setHPage(0); })}
          {filterBtn(hType === 'FULL',  t('AsmDashboard.filterFull'),  () => { setHType('FULL');  setHPage(0); })}
          {filterBtn(hType === 'DEEP',  t('AsmDashboard.filterDeep'),  () => { setHType('DEEP');  setHPage(0); })}
        </div>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('AsmDashboard.statusLabel')}</span>
          {filterBtn(hStatus === '',            t('AsmDashboard.all'),               () => { setHStatus('');            setHPage(0); })}
          {filterBtn(hStatus === 'COMPLETED',   t('AsmDashboard.statusCompleted'),   () => { setHStatus('COMPLETED');   setHPage(0); })}
          {filterBtn(hStatus === 'IN_PROGRESS', t('AsmDashboard.statusRunning'),     () => { setHStatus('IN_PROGRESS'); setHPage(0); })}
          {filterBtn(hStatus === 'FAILED',      t('AsmDashboard.statusFailed'),      () => { setHStatus('FAILED');      setHPage(0); })}
        </div>
        <span style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
          {t('AsmDashboard.scanCount', { count: totalElements })}
        </span>
        <button
          onClick={() => setRefreshTick(tk => tk + 1)}
          title={t('AsmDashboard.refreshScanHistory')}
          style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 6, fontSize: '0.78rem',
            border: '1.5px solid #e5e7eb', background: '#fff', color: '#374151',
            cursor: 'pointer', fontWeight: 500, lineHeight: 1.4 }}>
          {t('AsmDashboard.refresh')}
        </button>
      </div>

      {/* ── Table ── */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ ...S.table, opacity: hLoading ? 0.5 : 1, transition: 'opacity 0.15s' }}>
          <thead>
            <tr>
              {[
                { key: '#',           label: t('AsmDashboard.colNumber') },
                { key: 'domain',      label: t('AsmDashboard.colDomain') },
                { key: 'type',        label: t('AsmDashboard.colType') },
                { key: 'statusPhase', label: t('AsmDashboard.colStatusPhase') },
                { key: 'started',     label: t('AsmDashboard.colStarted') },
                { key: 'ended',       label: t('AsmDashboard.colEnded') },
                { key: 'duration',    label: t('AsmDashboard.colDuration') },
                { key: 'triggeredBy', label: t('AsmDashboard.colTriggeredBy') },
              ].map(h =>
                <th key={h.key} style={S.th}>{h.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {hLoading && content.length === 0 && (
              <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: '#9ca3af', padding: 40 }}>
                {t('AsmDashboard.loading')}
              </td></tr>
            )}
            {!hLoading && content.length === 0 && (
              <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: '#9ca3af', padding: 40 }}>
                {t('AsmDashboard.noScansMatchFilters')}
              </td></tr>
            )}
            {content.map(scan => {
              const sType = (scan.scanType || 'FULL').toUpperCase();
              return (
                <tr key={scan.id} style={S.tr}>
                  <td style={{ ...S.td, color: '#9ca3af' }}>{scan.id}</td>
                  <td style={S.td}><strong>{scan.domainName || '—'}</strong></td>
                  <td style={S.td}>
                    <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, padding: '2px 7px',
                      borderRadius: 5, background: (typeColor[sType] || '#6b7280') + '18',
                      color: typeColor[sType] || '#6b7280', border: `1px solid ${(typeColor[sType] || '#6b7280')}40` }}>
                      {typeIcon[sType] || ''} {sType}
                    </span>
                  </td>
                  <td style={S.td}><StatusPill status={scan.status} scanning={false} /></td>
                  <td style={S.td}>{fmtDate(scan.startTime)}</td>
                  <td style={S.td}>{fmtDate(scan.endTime)}</td>
                  <td style={S.td}>{scanDuration(scan)}</td>
                  <td style={S.td}>{scan.triggeredBy || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 16 }}>
          <button onClick={() => setHPage(0)} disabled={number === 0}
            style={{ ...paginBtn, opacity: number === 0 ? 0.35 : 1 }}>«</button>
          <button onClick={() => setHPage(p => p - 1)} disabled={number === 0}
            style={{ ...paginBtn, opacity: number === 0 ? 0.35 : 1 }}>‹</button>
          {Array.from({ length: totalPages }, (_, i) => i)
            .filter(i => Math.abs(i - number) <= 2)
            .map(i => (
              <button key={i} onClick={() => setHPage(i)}
                style={{ ...paginBtn,
                  background: i === number ? '#6366f1' : '#fff',
                  color:      i === number ? '#fff'    : '#374151',
                  border:     i === number ? '1.5px solid #6366f1' : '1.5px solid #e5e7eb',
                  fontWeight: i === number ? 700 : 400 }}>
                {i + 1}
              </button>
            ))}
          <button onClick={() => setHPage(p => p + 1)} disabled={number >= totalPages - 1}
            style={{ ...paginBtn, opacity: number >= totalPages - 1 ? 0.35 : 1 }}>›</button>
          <button onClick={() => setHPage(totalPages - 1)} disabled={number >= totalPages - 1}
            style={{ ...paginBtn, opacity: number >= totalPages - 1 ? 0.35 : 1 }}>»</button>
          <span style={{ fontSize: '0.75rem', color: '#9ca3af', marginLeft: 4 }}>
            {t('AsmDashboard.pageOfTotal', { page: number + 1, total: totalPages, count: totalElements })}
          </span>
        </div>
      )}
    </div>
  );
}


// ─── Style object ─────────────────────────────────────────────────────────────
const S = {
  page:      { padding: 24, background: '#f5f6f8', minHeight: '100vh', fontFamily: 'Inter, sans-serif' },
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 16 },
  title:     { fontSize: '1.45rem', fontWeight: 700, color: '#111827', margin: 0 },
  subtitle:  { fontSize: '0.82rem', color: '#6b7280', marginTop: 4, margin: 0 },
  addForm:   { display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 },
  domainInput:{ padding: '9px 14px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: '0.875rem', width: 240, outline: 'none', background: '#fff' },
  addBtn:    { padding: '9px 18px', background: '#4c75af', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600, whiteSpace: 'nowrap' },

  tabBar:    { display: 'flex', gap: 2, borderBottom: '2px solid #e5e7eb', marginBottom: 24, alignItems: 'center' },
  tab:       { padding: '10px 18px', background: 'none', border: 'none', borderBottom: '2px solid transparent', marginBottom: -2, cursor: 'pointer', fontSize: '0.875rem', color: '#6b7280', fontWeight: 500, transition: 'color 0.15s' },
  tabActive: { color: '#4c75af', borderBottom: '2px solid #4c75af', fontWeight: 700 },

  metricsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 14, marginBottom: 28 },
  metricCard: { background: '#fff', borderRadius: 10, padding: '18px 14px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #f0f0f0' },
  metricLabel:{ fontSize: '0.7rem', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 4 },

  sectionHeading: { fontSize: '0.95rem', fontWeight: 700, color: '#374151', marginBottom: 14, marginTop: 0 },

  domainGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 },
  domainCard: { background: '#fff', borderRadius: 10, padding: 16, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  domainCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 },
  domainCardName: { fontWeight: 700, color: '#111827', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
  domainCardStats:{ fontSize: '0.78rem', color: '#6b7280', display: 'flex', gap: 5, marginBottom: 6 },
  domainCardMeta: { fontSize: '0.72rem', color: '#9ca3af', marginBottom: 12 },
  domainCardActions: { display: 'flex', gap: 6 },
  dot: { color: '#d1d5db' },
  cardBtn:   { flex: 1, padding: '6px 0', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 },

  card:      { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', overflow: 'hidden' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  th:        { background: '#f8f9fa', padding: '11px 16px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid #e5e7eb' },
  td:        { padding: '12px 16px', fontSize: '0.875rem', color: '#374151', borderBottom: '1px solid #f3f4f6', textAlign: 'left' },
  tr:        { transition: 'background 0.1s' },
  iconBtn:   (color) => ({ background: 'none', border: 'none', cursor: 'pointer', color, fontSize: '1rem', padding: '4px 7px', borderRadius: 4, marginLeft: 2 }),

  empty:     { background: '#fff', borderRadius: 10, padding: '60px 20px', textAlign: 'center', color: '#9ca3af', fontSize: '0.9rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },

  // Findings
  filterLabel: { fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 },
  toolbar:   { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' },
  select:    { padding: '9px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: '0.875rem', minWidth: 220, background: '#fff', color: '#374151' },
  searchInput:{ padding: '9px 12px', borderRadius: 8, border: '1.5px solid #d1d5db', fontSize: '0.875rem', flex: 1, minWidth: 180, background: '#fff' },
  sevFilterRow:{ display: 'flex', gap: 4 },
  sevBtn:    { padding: '7px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700, transition: 'all 0.15s' },

  cveSummaryRow: { display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  cveSumCard:    { background: '#fff', borderRadius: 8, padding: '12px 18px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', minWidth: 80 },

  subdomainBlock:  { background: '#fff', borderRadius: 10, marginBottom: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' },
  subdomainHeader: { padding: '13px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, background: '#f8f9fb', userSelect: 'none' },
  chevron:         { color: '#9ca3af', fontSize: '1rem', width: 16 },
  subdomainMeta:   { marginLeft: 'auto', display: 'flex', gap: 6 },
  metaChip:        { fontSize: '0.72rem', color: '#6b7280', background: '#f3f4f6', padding: '2px 9px', borderRadius: 10, border: '1px solid #e5e7eb' },

  ipBlock:   { padding: '10px 18px', borderTop: '1px solid #f3f4f6' },
  ipRow:     { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: '0.875rem' },
  ipIcon:    { color: '#4c75af' },
  portGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 10 },

  portCard:       { border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#fafafa' },
  portCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  portNum:        { fontWeight: 800, fontSize: '1rem', color: '#1e3a5f' },
  portProto:      { fontWeight: 400, fontSize: '0.75rem', color: '#9ca3af' },
  openBadge:      { fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', background: '#dcfce7', color: '#16a34a', padding: '2px 7px', borderRadius: 4 },

  appRow:     { display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 6 },
  appName:    { fontSize: '0.82rem', fontWeight: 600, color: '#374151' },
  appVersion: { fontSize: '0.75rem', color: '#9ca3af' },

  cveCard:    { padding: '8px 10px', borderRadius: 6, marginBottom: 6 },
  cveTopRow:  { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 },
  scoreChip:  { fontSize: '0.68rem', color: '#6b7280', background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 },
  cveDesc:    { fontSize: '0.73rem', color: '#6b7280', margin: '3px 0 0', lineHeight: 1.45 },
  cweTagRow:  { display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 5 },
  cweTag:     { fontSize: '0.62rem', color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', padding: '1px 6px', borderRadius: 4, fontWeight: 600, fontFamily: 'monospace' },
  penTestRow: { marginTop: 6, padding: '4px 8px', borderRadius: 4, fontSize: '0.75rem', lineHeight: 1.45, border: '1px solid transparent' },

  // Domains tab
  metricsStrip: { display: 'flex', alignItems: 'center', gap: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 16, overflow: 'hidden' },
  metricItem:   { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, padding: '12px 8px', borderRight: '1px solid #f1f5f9' },
  domBtn: (variant) => {
    const base = { padding: '7px 12px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', transition: 'opacity 0.15s' };
    const map = {
      primary: { ...base, background: '#4c75af', color: '#fff' },
      light:   { ...base, background: '#0369a1', color: '#fff' },
      full:    { ...base, background: '#16a34a', color: '#fff' },
      rescan:  { ...base, background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db' },
      deep:    { ...base, background: '#7c3aed', color: '#fff' },
      delete:  { ...base, background: 'none', color: '#ef4444', border: '1px solid #fca5a5', padding: '7px 9px' },
    };
    return map[variant] || base;
  },
};

// ─── Red Team Intelligence styles ─────────────────────────────────────────────
const RT = {
  controlPanel:  { display: 'flex', alignItems: 'flex-start', gap: 24, background: 'linear-gradient(135deg,#1e3a5f 0%,#162d4a 100%)', borderRadius: 14, padding: '22px 26px', marginBottom: 24, boxShadow: '0 4px 16px rgba(30,58,95,0.25)' },
  panelTitle:    { fontSize: '1.1rem', fontWeight: 800, color: '#fff', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 },
  panelSubtitle: { fontSize: '0.78rem', color: '#93c5fd', lineHeight: 1.5 },
  targetIcon:    { fontSize: '1.3rem' },
  controlRight:  { display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end', flexShrink: 0 },
  select:        { padding: '9px 12px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,0.2)', fontSize: '0.875rem', minWidth: 240, background: 'rgba(255,255,255,0.1)', color: '#fff', outline: 'none' },
  sevRow:        { display: 'flex', gap: 6 },
  runBtn:        { padding: '10px 22px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontSize: '0.875rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' },
  spin:          { display: 'inline-block', animation: 'spin 1s linear infinite' },

  loadingBox:    { background: '#fff', borderRadius: 12, padding: '60px 20px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 16 },
  emptyBox:      { background: '#fff', borderRadius: 12, padding: '60px 20px', textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', color: '#9ca3af' },

  summaryStrip:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', borderRadius: 10, padding: '14px 20px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', flexWrap: 'wrap', gap: 10 },
  summaryLeft:   { display: 'flex', alignItems: 'center', gap: 12 },
  domainChip:    { background: '#eff6ff', color: '#1e40af', padding: '4px 12px', borderRadius: 10, fontSize: '0.82rem', fontWeight: 700 },
  simLabel:      { fontSize: '0.78rem', color: '#6b7280' },
  sevBubble:     { borderRadius: 10, padding: '6px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, minWidth: 54 },

  sevGroupHeader:{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: '#fff', borderRadius: '10px 10px 0 0', marginBottom: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  sevDot:        { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },

  pocCard:       { background: '#fff', borderRadius: 10, marginBottom: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', transition: 'box-shadow 0.2s' },
  pocHeader:     { padding: '14px 18px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: '#fafafa', userSelect: 'none', gap: 12 },
  pocHeaderLeft: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flex: 1 },
  pocHeaderRight:{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  sevBadge:      { color: '#fff', fontSize: '0.62rem', fontWeight: 800, padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.06em', flexShrink: 0 },
  pocTemplateId: { fontWeight: 700, fontSize: '0.82rem', color: '#1e3a5f', fontFamily: 'monospace' },
  pocSubdomain:  { fontSize: '0.75rem', color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: 6 },
  mitreChip:     { fontSize: '0.68rem', color: '#7c3aed', background: '#f5f3ff', border: '1px solid #ddd6fe', padding: '2px 8px', borderRadius: 6, fontWeight: 600 },
  chevronIcon:   { color: '#9ca3af', fontSize: '1rem', width: 16 },

  narrative:     { padding: '10px 18px 14px', fontSize: '0.82rem', color: '#374151', lineHeight: 1.6, borderBottom: '1px solid #f3f4f6' },

  pocDetail:     { padding: '18px 20px', background: '#fafeff' },

  impactRow:     { display: 'flex', alignItems: 'flex-start', gap: 8, background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 14 },
  impactLabel:   { fontSize: '0.75rem', fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap', marginTop: 2 },
  impactText:    { fontSize: '0.78rem', color: '#7f1d1d', lineHeight: 1.5 },

  sectionLabel:  { fontSize: '0.68rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 },

  scenarioBox:   { background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: '12px 16px' },
  scenarioLine:  { fontSize: '0.8rem', color: '#374151', lineHeight: 1.7, paddingLeft: 4 },

  terminal:      { background: '#0f172a', borderRadius: 10, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' },
  terminalDots:  { padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, background: '#1e293b', borderBottom: '1px solid #334155' },
  dot3:          { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  terminalCode:  { margin: 0, padding: '14px 18px', color: '#4ade80', fontSize: '0.8rem', fontFamily: "'Fira Code', 'Courier New', monospace", whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.7 },

  evidenceBox:   { background: '#0f172a', borderRadius: 8, border: '1px solid #1e293b', maxHeight: 280, overflow: 'auto' },
  evidencePre:   { margin: 0, padding: '14px 18px', color: '#cbd5e1', fontSize: '0.75rem', fontFamily: "'Fira Code', 'Courier New', monospace", whiteSpace: 'pre-wrap', wordBreak: 'break-all', lineHeight: 1.65 },

  bottomRow:     { borderTop: '1px solid #f1f5f9', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 },
  mitreRow:      { display: 'flex', gap: 8, alignItems: 'flex-start' },
  mitreLabel:    { fontSize: '0.68rem', fontWeight: 700, color: '#7c3aed', whiteSpace: 'nowrap', marginTop: 1, textTransform: 'uppercase', letterSpacing: '0.06em' },
  mitreValue:    { fontSize: '0.75rem', color: '#4c1d95', fontWeight: 500 },
  remediationRow:{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '8px 12px' },
  remLabel:      { fontSize: '0.72rem', fontWeight: 700, color: '#15803d', whiteSpace: 'nowrap' },
  remText:       { fontSize: '0.75rem', color: '#14532d', lineHeight: 1.5 },
  // Extracted Artifacts table
  artifactTable: { borderRadius: 8, border: '1px solid #1e3a5f', overflow: 'hidden', fontSize: '0.76rem' },
  artifactRow:   { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', borderBottom: '1px solid #1e293b' },
  artifactType:  { minWidth: 180, flexShrink: 0 },
  artifactTypeBadge: { display: 'inline-block', background: '#1e3a5f', color: '#93c5fd', borderRadius: 4, padding: '2px 7px', fontSize: '0.67rem', fontWeight: 700, letterSpacing: '0.02em', wordBreak: 'break-word' },
  artifactContent: { flex: 1, minWidth: 0 },
  artifactValue: { color: '#f1f5f9', fontFamily: "'Fira Code', 'Courier New', monospace", fontSize: '0.73rem', wordBreak: 'break-all', fontWeight: 600 },
  artifactMeta:  { marginTop: 2, color: '#64748b', fontSize: '0.68rem', wordBreak: 'break-word' },
  artifactLoc:   { color: '#38bdf8', fontFamily: "'Fira Code', monospace" },
  artifactCtx:   { color: '#64748b' },
};
